import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiDebugTarget, formatErr, resolveMediaUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Pencil, Trash2, Search, Upload, Download, Monitor, PhoneCall, Check, X, Settings, Users, ShieldCheck, MessageCircle, Mic, Volume2, ReceiptText, Send, IndianRupee, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const ownerBlank = {
  wing: "A",
  floor: "",
  flatNumber: "",
  areaSqFt: "",
  ownerName: "",
  ownerMobile: "",
  ownerSecondaryMobile: "",
  preferredVisitorMobile: "primary",
  ownerEmail: "",
  familyMembers: "",
  tenantName: "",
  tenantMobile: "",
  parkingNumber: "",
  vehicleNumber: "",
  vehicles: [],
  vehicleCycleCount: "0",
  ownerPhoto: "",
  intercomNumber: "",
  emergencyContact: "",
  committeeRole: "",
  committeeTitle: "",
  unitType: "residential",
  occupancyStatus: "owner_occupied",
  occupancyEffectiveFrom: "",
  tenantDeedStatus: "pending",
  tenantDeedSubmittedOn: "",
  chargeApplicabilityOverrides: {},
  openingOutstanding: "",
  openingOutstandingSide: "debit",
  openingPrincipalOutstanding: "",
  openingInterestOutstanding: "",
  openingOutstandingNote: "",
  openingOutstandingAsOf: "",
  isActive: true,
};

const committeeRoles = [
  { value: "none", label: "No committee role" },
  { value: "chairman", label: "Chairman" },
  { value: "secretary", label: "Secretary" },
  { value: "treasurer", label: "Treasurer" },
  { value: "committee_member", label: "Committee Member" },
];

const societyStaffRoleLabels = {
  security: "Guard / Security",
  office_staff: "Office Staff",
};

const societyMemberRoleLabels = {
  flat_owner: "Flat Owner",
  chairman: "Chairman",
  secretary: "Secretary",
  treasurer: "Treasurer",
  committee_member: "Committee member",
};

const visitorBlank = {
  visitorName: "",
  visitorMobile: "",
  visitorPhoto: "",
  purpose: "",
  wing: "A",
  flatNumber: "",
  vehicleNumber: "",
  visitorCount: 1,
  gateName: "",
  securityName: "",
  remarks: "",
};

const settingsBlank = {
  enableDirectoryZone: true,
  enableVisitorPendingZone: true,
  directoryDisplayMode: "wing_rotation",
  selectedWing: "All",
  rotationInterval: 15,
  rowsPerPage: 8,
  fontSize: 24,
  showOwnerPhoto: true,
  showMobile: false,
  showFlatNumber: true,
  displayStyle: "card",
  backgroundColor: "#07111f",
  textColor: "#ffffff",
  promptDurationSeconds: 30,
};

const noticeBlank = {
  title: "",
  body: "",
  image_url: "",
  category: "notice",
  severity: "normal",
  active: true,
};

const billingSettingsBlank = {
  billingDay: 1,
  dueDays: 15,
  paymentDueDay: 15,
  interestRateAnnual: 0,
  interestRoundingMethod: "nearest",
  billPrefix: "MAINT",
  nextBillNumber: 1,
  bankName: "",
  bankAccountName: "",
  bankAccountNumber: "",
  ifsc: "",
  upiId: "",
  autoBillingEnabled: false,
  autoWhatsappEnabled: false,
  autoWhatsappPremiumEnabled: false,
  whatsappEnabled: false,
  whatsappTemplateName: "",
  whatsappTemplateLanguage: "en_US",
  notes: "",
  customFields: [],
};

const chargeHeadBlank = {
  name: "",
  code: "",
  category: "regular",
  defaultAmount: 0,
  allocation: "flat",
  unitGroup: "flat",
  applicability: "all",
  taxable: false,
  active: true,
};

const standardChargeTemplates = [
  { name: "Maintenance charge", code: "MAINTENANCE", category: "regular", allocation: "area", label: "Maintenance / sq ft" },
  { name: "Water charges", code: "WATER", category: "utility", allocation: "flat", label: "Water fixed" },
  { name: "Sinking fund", code: "SINKING", category: "fund", allocation: "flat", label: "Sinking fund" },
  { name: "Repairs and maintenance fund", code: "REPAIR", category: "fund", allocation: "flat", label: "Repair fund" },
  { name: "Parking - 2 wheeler", code: "PARKING_2W", category: "regular", allocation: "vehicle_2w", label: "Parking / 2-wheeler" },
  { name: "Parking - 4 wheeler", code: "PARKING_4W", category: "regular", allocation: "vehicle_4w", label: "Parking / 4-wheeler" },
  { name: "Parking - cycle", code: "PARKING_CYCLE", category: "regular", allocation: "vehicle_cycle", label: "Parking / cycle" },
  { name: "Other approved charges", code: "OTHER", category: "other", allocation: "flat", label: "Other" },
];

const auditorBlank = {
  name: "",
  email: "",
  password: "",
  phone: "",
  firmName: "",
  membershipNo: "",
  status: "active",
};

const staffBlank = {
  name: "",
  email: "",
  password: "",
  phone: "",
  societyRole: "security",
  status: "active",
};

const memberBlank = {
  ownerId: "",
  name: "",
  email: "",
  password: "",
  phone: "",
  societyRole: "flat_owner",
  status: "active",
};

const societyTabs = new Set(["directory", "logins", "notices", "visitor", "pending", "history", "billing", "display", "voice"]);
const billingPages = new Set(["charges", "onetime", "opening", "settings", "generate", "outstanding", "payments", "whatsapp"]);

const initialSocietyTab = () => {
  if (typeof window === "undefined") return "directory";
  const urlTab = new URLSearchParams(window.location.search).get("tab");
  if (societyTabs.has(urlTab)) return urlTab;
  const savedTab = window.localStorage.getItem("rp_society_active_tab");
  return societyTabs.has(savedTab) ? savedTab : "directory";
};

const initialBillingPage = () => {
  if (typeof window === "undefined") return "charges";
  const urlPage = new URLSearchParams(window.location.search).get("billingPage");
  if (billingPages.has(urlPage)) return urlPage;
  const savedPage = window.localStorage.getItem("rp_society_billing_page");
  return billingPages.has(savedPage) ? savedPage : "charges";
};

const financialCloseBlank = {
  fromDate: `${new Date().getFullYear()}-04-01`,
  toDate: `${new Date().getFullYear() + 1}-03-31`,
  reason: "",
};

function allocationRateLabel(allocation) {
  if (allocation === "area") return "Rate per sq ft";
  if (allocation === "vehicle_2w") return "Rate per 2-wheeler";
  if (allocation === "vehicle_4w") return "Rate per 4-wheeler";
  if (allocation === "vehicle_cycle") return "Rate per cycle";
  return "Fixed amount per flat";
}

function monthValue() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function previousMonthValue(month) {
  const [year, monthNumber] = String(month || currentMonthValue()).split("-").map(Number);
  const d = new Date(year, monthNumber - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [year, monthNumber] = String(month || "").split("-").map(Number);
  if (!year || !monthNumber) return "";
  return new Date(year, monthNumber - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function blankVehicle(type) {
  return { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, type, number: "", nonRtoEv: false };
}

function toOwnerPayload(form) {
  return {
    ...form,
    areaSqFt: Number(form.areaSqFt) || 0,
    // Cycles never carry a registration number, so they stay a plain count.
    // 2-wheelers/4-wheelers are individual rows with their own plate number -
    // the backend derives vehicle2wCount/vehicle4wCount from this list.
    vehicles: (form.vehicles || []).map((v) => ({
      type: v.type === "4w" ? "4w" : "2w",
      number: String(v.number || "").trim().toUpperCase(),
      nonRtoEv: v.type === "2w" ? Boolean(v.nonRtoEv) : false,
    })),
    vehicleCycleCount: Math.max(0, Number(form.vehicleCycleCount) || 0),
    openingOutstanding: Math.max(0, Number(form.openingOutstanding) || 0),
    openingOutstandingSide: form.openingOutstandingSide === "credit" ? "credit" : "debit",
    openingPrincipalOutstanding: form.openingOutstandingSide === "credit" ? 0 : Math.max(0, Number(form.openingPrincipalOutstanding) || 0),
    openingInterestOutstanding: form.openingOutstandingSide === "credit" ? 0 : Math.max(0, Number(form.openingInterestOutstanding) || 0),
    unitType: ["residential", "shop", "office", "other"].includes(form.unitType) ? form.unitType : "residential",
    occupancyStatus: ["owner_occupied", "tenant_occupied", "vacant"].includes(form.occupancyStatus) ? form.occupancyStatus : "owner_occupied",
    occupancyEffectiveFrom: form.occupancyEffectiveFrom || "",
    tenantDeedStatus: form.tenantDeedStatus === "submitted" ? "submitted" : "pending",
    tenantDeedSubmittedOn: form.tenantDeedStatus === "submitted" ? (form.tenantDeedSubmittedOn || "") : "",
    chargeApplicabilityOverrides: Object.fromEntries(
      Object.entries(form.chargeApplicabilityOverrides || {}).filter(([, value]) => value === "include" || value === "exclude")
    ),
    intercomNumber: form.intercomNumber || form.flatNumber,
    committeeRole: form.committeeRole === "none" ? "" : form.committeeRole,
    familyMembers: String(form.familyMembers || "")
      .split(/[,\n]/)
      .map((x) => x.trim())
      .filter(Boolean),
    isActive: Boolean(form.isActive),
  };
}

function ownerToForm(owner) {
  return {
    ...ownerBlank,
    ...owner,
    vehicles: Array.isArray(owner.vehicles) && owner.vehicles.length
      ? owner.vehicles.map((v) => ({ id: v.id || blankVehicle(v.type).id, type: v.type === "4w" ? "4w" : "2w", number: v.number || "", nonRtoEv: Boolean(v.nonRtoEv) }))
      : [
          ...Array.from({ length: Number(owner.vehicle2wCount || 0) }, () => blankVehicle("2w")),
          ...Array.from({ length: Number(owner.vehicle4wCount || 0) }, () => blankVehicle("4w")),
        ],
    familyMembers: Array.isArray(owner.familyMembers) ? owner.familyMembers.join(", ") : owner.familyMembers || "",
    openingPrincipalOutstanding: owner.openingOutstandingSide === "credit" ? "" : (owner.openingPrincipalOutstanding ?? owner.openingOutstanding ?? ""),
    openingInterestOutstanding: owner.openingOutstandingSide === "credit" ? "" : (owner.openingInterestOutstanding || ""),
    isActive: owner.isActive !== false,
  };
}

function ownerLabel(owner) {
  if (!owner) return "No owner found";
  return `${owner.ownerName || "Resident"} - ${owner.wing || ""} ${owner.flatNumber || ""}`.trim();
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">{label}</Label>
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="text-2xl font-extrabold text-[#111827]">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[#6B7280]">{label}</div>
    </div>
  );
}

export default function HousingSocietyPanel() {
  const { user } = useAuth();
  const commercialState = user?.societyCommercialState;
  const setupMode = commercialState && commercialState.legacy !== true && commercialState.operational_active !== true;
  const [owners, setOwners] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [notices, setNotices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [settings, setSettings] = useState(settingsBlank);
  const [billingRule, setBillingRule] = useState(null);
  const [billingSettings, setBillingSettings] = useState(billingSettingsBlank);
  const [chargeHeads, setChargeHeads] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [billAdjustments, setBillAdjustments] = useState([]);
  const [yearEndReport, setYearEndReport] = useState({ incomeExpense: {}, balanceSheet: {}, fundBalances: [], trialBalance: [] });
  const [billingRuns, setBillingRuns] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [bankRows, setBankRows] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [financialCloses, setFinancialCloses] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [memberUsers, setMemberUsers] = useState([]);
  const [chargeForm, setChargeForm] = useState(chargeHeadBlank);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [chargeGroup, setChargeGroup] = useState("flat");
  const [auditorForm, setAuditorForm] = useState(auditorBlank);
  const [staffForm, setStaffForm] = useState(staffBlank);
  const [memberForm, setMemberForm] = useState(memberBlank);
  const [editingStaffLogin, setEditingStaffLogin] = useState(null);
  const [editingMemberLogin, setEditingMemberLogin] = useState(null);
  const [openingDueDrafts, setOpeningDueDrafts] = useState({});
  const [financialCloseForm, setFinancialCloseForm] = useState(financialCloseBlank);
  const [bankCsvText, setBankCsvText] = useState("");
  const [bankCsvFile, setBankCsvFile] = useState(null);
  const [billingMonth, setBillingMonth] = useState(monthValue());
  const [generationMonth, setGenerationMonth] = useState(currentMonthValue());
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [selectedBillCharge, setSelectedBillCharge] = useState({ name: "", amount: "", reason: "" });
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
  const [bulkOwnerForm, setBulkOwnerForm] = useState({ unitType: "unchanged", occupancyStatus: "unchanged", tenantDeedStatus: "unchanged", floor: "", isActive: "unchanged" });
  const [oneTimeChargeForm, setOneTimeChargeForm] = useState({ month: monthValue(), reason: "", amount: "", adjustmentType: "debit", target: "residential" });
  const [oneTimeOwnerIds, setOneTimeOwnerIds] = useState([]);
  const [q, setQ] = useState("");
  const [loginSearch, setLoginSearch] = useState("");
  const [noticeSearch, setNoticeSearch] = useState("");
  const [visitorSearch, setVisitorSearch] = useState("");
  const [billSearch, setBillSearch] = useState("");
  const [outstandingSearch, setOutstandingSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [whatsappSearch, setWhatsappSearch] = useState("");
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [wingFilter, setWingFilter] = useState("All");
  const [activeSocietyTab, setActiveSocietyTab] = useState(initialSocietyTab);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [billEditorOpen, setBillEditorOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [billLineDrafts, setBillLineDrafts] = useState([]);
  const [billInterestDraft, setBillInterestDraft] = useState(0);
  const [billEditReason, setBillEditReason] = useState("");
  const [editingOwner, setEditingOwner] = useState(null);
  const [editingNotice, setEditingNotice] = useState(null);
  const [ownerForm, setOwnerForm] = useState(ownerBlank);
  const [noticeForm, setNoticeForm] = useState(noticeBlank);
  const [noticeValidity, setNoticeValidity] = useState("infinite");
  const [noticeCustomDate, setNoticeCustomDate] = useState("");
  const [visitorForm, setVisitorForm] = useState(visitorBlank);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [displayForm, setDisplayForm] = useState({ ownerName: "", wing: "", flatNumber: "", deviceId: "", durationSeconds: 30 });
  const [voiceText, setVoiceText] = useState("Hello RP, show flat A 101");
  const [agentAnswer, setAgentAnswer] = useState("");
  const [agentDisplay, setAgentDisplay] = useState(null);
  const [listening, setListening] = useState(false);
  const [ttsStatus, setTtsStatus] = useState("");
  const advancedAccountingOpen = true;
  const [billingMenu, setBillingMenu] = useState(initialBillingPage);
  const [busy, setBusy] = useState(false);

  const selectSocietyTab = useCallback((tab) => {
    const nextTab = societyTabs.has(tab) ? tab : "directory";
    setActiveSocietyTab(nextTab);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("rp_society_active_tab", nextTab);
      const url = new URL(window.location.href);
      url.searchParams.set("tab", nextTab);
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  const selectBillingPage = useCallback((page) => {
    const nextPage = billingPages.has(page) ? page : "charges";
    setBillingMenu(nextPage);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("rp_society_billing_page", nextPage);
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "billing");
      url.searchParams.set("billingPage", nextPage);
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [directoryRes, visitorsRes, settingsRes, devicesRes, noticesRes, ruleRes, billingSettingsRes, chargeHeadsRes, billsRes, paymentsRes, adjustmentsRes, yearEndRes, billingRunsRes, whatsappLogsRes, bankRowsRes, auditLogsRes, closesRes, attachmentsRes, auditorsRes, staffRes, membersRes] = await Promise.all([
        api.get("/society/directory"),
        api.get("/society/visitors"),
        api.get("/society/directory-settings"),
        api.get("/client/devices").catch(() => ({ data: [] })),
        api.get("/client/notices").catch(() => ({ data: [] })),
        api.get("/society/billing/rules/current").catch((err) => ({ data: { rule: { implemented: false, message: formatErr(err, "State rule unavailable") } } })),
        api.get("/society/billing/settings").catch(() => ({ data: billingSettingsBlank })),
        api.get("/society/billing/charge-heads").catch(() => ({ data: [] })),
        api.get("/society/billing/bills").catch(() => ({ data: [] })),
        api.get("/society/billing/payments").catch(() => ({ data: [] })),
        api.get("/society/billing/adjustments").catch(() => ({ data: [] })),
        api.get("/society/reports/year-end").catch(() => ({ data: { incomeExpense: {}, balanceSheet: {}, fundBalances: [], trialBalance: [] } })),
        api.get("/society/billing/scheduler/runs").catch(() => ({ data: [] })),
        api.get("/society/whatsapp/logs").catch(() => ({ data: [] })),
        api.get("/society/bank-statements").catch(() => ({ data: [] })),
        api.get("/society/audit/logs").catch(() => ({ data: [] })),
        api.get("/society/financial-closes").catch(() => ({ data: [] })),
        api.get("/society/attachments").catch(() => ({ data: [] })),
        api.get("/society/auditors").catch(() => ({ data: [] })),
        api.get("/society/staff").catch(() => ({ data: [] })),
        api.get("/society/members").catch(() => ({ data: [] })),
      ]);
      setOwners(Array.isArray(directoryRes.data) ? directoryRes.data : []);
      setVisitors(Array.isArray(visitorsRes.data) ? visitorsRes.data : []);
      setSettings({ ...settingsBlank, ...(settingsRes.data || {}) });
      setDevices(Array.isArray(devicesRes.data) ? devicesRes.data : []);
      setNotices(Array.isArray(noticesRes.data) ? noticesRes.data : []);
      setBillingRule(ruleRes.data?.rule || null);
      setBillingSettings({ ...billingSettingsBlank, ...(billingSettingsRes.data || {}) });
      setChargeHeads(Array.isArray(chargeHeadsRes.data) ? chargeHeadsRes.data : []);
      setBills(Array.isArray(billsRes.data) ? billsRes.data : []);
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setBillAdjustments(Array.isArray(adjustmentsRes.data) ? adjustmentsRes.data : []);
      setYearEndReport(yearEndRes.data || { incomeExpense: {}, balanceSheet: {}, fundBalances: [], trialBalance: [] });
      setBillingRuns(Array.isArray(billingRunsRes.data) ? billingRunsRes.data : []);
      setWhatsappLogs(Array.isArray(whatsappLogsRes.data) ? whatsappLogsRes.data : []);
      setBankRows(Array.isArray(bankRowsRes.data) ? bankRowsRes.data : []);
      setAuditLogs(Array.isArray(auditLogsRes.data) ? auditLogsRes.data : []);
      setFinancialCloses(Array.isArray(closesRes.data) ? closesRes.data : []);
      setAttachments(Array.isArray(attachmentsRes.data) ? attachmentsRes.data : []);
      setAuditors(Array.isArray(auditorsRes.data) ? auditorsRes.data : []);
      setStaffUsers(Array.isArray(staffRes.data) ? staffRes.data : []);
      setMemberUsers(Array.isArray(membersRes.data) ? membersRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Unable to load society module"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const drafts = {};
    owners.forEach((owner) => {
      drafts[owner.id] = {
        amount: owner.openingOutstandingSide === "credit" ? (owner.openingOutstanding || "") : "",
        principalAmount: owner.openingOutstandingSide === "credit" ? "" : (owner.openingPrincipalOutstanding ?? owner.openingOutstanding ?? ""),
        interestAmount: owner.openingOutstandingSide === "credit" ? "" : (owner.openingInterestOutstanding || ""),
        side: owner.openingOutstandingSide === "credit" ? "credit" : "debit",
        note: owner.openingOutstandingNote || "",
        asOfDate: owner.openingOutstandingAsOf || "",
      };
    });
    setOpeningDueDrafts(drafts);
  }, [owners]);

  const wings = useMemo(() => {
    const values = Array.from(new Set(owners.map((o) => String(o.wing || "").trim()).filter(Boolean)));
    return ["All", ...values.sort()];
  }, [owners]);

  const filteredOwners = useMemo(() => {
    const search = q.trim().toLowerCase();
    return owners.filter((owner) => {
      const matchesWing = wingFilter === "All" || owner.wing === wingFilter;
      const haystack = `${owner.ownerName || ""} ${owner.ownerMobile || ""} ${owner.flatNumber || ""} ${owner.wing || ""} ${owner.tenantName || ""} ${owner.committeeRole || ""} ${owner.committeeTitle || ""}`.toLowerCase();
      return matchesWing && (!search || haystack.includes(search));
    });
  }, [owners, q, wingFilter]);
  const pendingVisitors = visitors.filter((v) => v.status === "pending");
  const historyVisitors = visitors.filter((v) => v.status !== "pending");
  const matchesSearch = (row, fields, search) => {
    const needle = search.trim().toLowerCase();
    return !needle || fields.map((field) => row?.[field] || "").join(" ").toLowerCase().includes(needle);
  };
  const filteredMemberUsers = memberUsers.filter((row) => matchesSearch(row, ["name", "wing", "flatNumber", "phone", "ownerMobile", "email", "societyRole", "status"], loginSearch));
  const filteredStaffUsers = staffUsers.filter((row) => matchesSearch(row, ["name", "phone", "email", "societyRole", "status"], loginSearch));
  const filteredNotices = notices.filter((row) => matchesSearch(row, ["title", "body", "category", "severity", "createdByName"], noticeSearch));
  const filteredPendingVisitors = pendingVisitors.filter((row) => matchesSearch(row, ["visitorName", "visitorMobile", "wing", "flatNumber", "ownerName", "purpose"], visitorSearch));
  const filteredHistoryVisitors = historyVisitors.filter((row) => matchesSearch(row, ["visitorName", "visitorMobile", "wing", "flatNumber", "ownerName", "purpose", "status"], visitorSearch));
  const selectedVisitorOwner = owners.find((o) => o.wing === visitorForm.wing && o.flatNumber === String(visitorForm.flatNumber || "").toUpperCase());
  const monthBills = bills.filter((bill) => !billingMonth || bill.billingMonth === billingMonth);
  const billDraftSubtotal = billLineDrafts.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const billDraftOpening = billLineDrafts.reduce((sum, line) => sum + ((line.category === "opening" || line.code === "OPENING") ? Number(line.amount || 0) : 0), 0);
  const billDraftCurrentCharges = billLineDrafts.reduce((sum, line) => sum + ((!['opening', 'opening_credit'].includes(line.category)) ? Number(line.amount || 0) : 0), 0);
  const billDraftTotal = Math.max(0, billDraftSubtotal + Number(editingBill?.arrears || 0) + Number(billInterestDraft || 0));
  const billDraftBalance = Math.max(0, billDraftTotal - Number(editingBill?.paidAmount || 0));
  const openingDueTotal = owners.reduce((sum, owner) => {
    const draft = openingDueDrafts[owner.id] || {};
    return sum + (draft.side === "credit" ? 0 : Number(draft.principalAmount || 0) + Number(draft.interestAmount || 0));
  }, 0);
  const openingPrincipalTotal = owners.reduce((sum, owner) => {
    const draft = openingDueDrafts[owner.id] || {};
    return sum + (draft.side === "credit" ? 0 : Number(draft.principalAmount || 0));
  }, 0);
  const openingInterestTotal = owners.reduce((sum, owner) => {
    const draft = openingDueDrafts[owner.id] || {};
    return sum + (draft.side === "credit" ? 0 : Number(draft.interestAmount || 0));
  }, 0);
  const openingAdvanceTotal = owners.reduce((sum, owner) => {
    const draft = openingDueDrafts[owner.id] || {};
    return sum + (draft.side === "credit" ? Number(draft.amount || 0) : 0);
  }, 0);
  const outstandingTotal = bills.reduce((sum, bill) => sum + Number(bill.balanceDue || 0), 0);
  const ownersOutstanding = (() => {
    const map = new Map();
    for (const bill of bills) {
      const due = Number(bill.balanceDue || 0);
      if (due <= 0 || bill.status === "cancelled") continue;
      const key = bill.ownerId || `${bill.wing || ""}|${bill.flatNumber || ""}`;
      const entry = map.get(key) || {
        key, wing: bill.wing, flatNumber: bill.flatNumber, ownerName: bill.ownerName, ownerMobile: bill.ownerMobile,
        outstanding: 0, unpaidBills: 0, latestBill: bill,
      };
      entry.outstanding += due;
      entry.unpaidBills += 1;
      if (!entry.latestBill || String(bill.billingMonth || "") > String(entry.latestBill.billingMonth || "")) entry.latestBill = bill;
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) => b.outstanding - a.outstanding);
  })();
  const filteredMonthBills = monthBills.filter((row) => matchesSearch(row, ["billNumber", "ownerName", "ownerMobile", "wing", "flatNumber", "status"], billSearch));
  const filteredOwnersOutstanding = ownersOutstanding.filter((row) => matchesSearch(row, ["ownerName", "ownerMobile", "wing", "flatNumber"], outstandingSearch));
  const filteredPayments = payments.filter((row) => (paymentStatusFilter === "all" || row.status === paymentStatusFilter) && matchesSearch(row, ["billNumber", "ownerName", "utr", "status", "receiptNumber"], paymentSearch));
  const filteredWhatsappLogs = whatsappLogs.filter((row) => matchesSearch(row, ["messageType", "billNumber", "receiptNumber", "phone", "status", "failureReason", "error"], whatsappSearch));
  const pendingPayments = payments.filter((payment) => payment.status === "pending_verification");
  const failedWhatsappCount = whatsappLogs.filter((log) => log.status === "failed").length;
  const activeAreaCharges = chargeHeads.filter((head) => head.active !== false && head.allocation === "area");
  const visibleChargeHeads = chargeHeads.filter((head) => (head.unitGroup || "flat") === chargeGroup);
  const selectedOwnerChargeGroup = ownerForm.unitType === "shop" ? "shop" : ownerForm.unitType === "office" ? "office" : "flat";
  const flatsMissingArea = activeAreaCharges.length ? owners.filter((owner) => {
    if (owner.isActive === false || Number(owner.areaSqFt || 0)) return false;
    const ownerGroup = owner.unitType === "shop" ? "shop" : owner.unitType === "office" ? "office" : "flat";
    return activeAreaCharges.some((head) => (head.unitGroup || "flat") === ownerGroup);
  }).length : 0;
  const launchChecklist = [
    { label: "Owner directory", ok: owners.length > 0, detail: `${owners.length} flats` },
    { label: "Billing settings", ok: !!billingSettings.billPrefix && Number(billingSettings.dueDays || 0) >= 0, detail: billingSettings.billPrefix || "Not set" },
    { label: "Charge heads", ok: chargeHeads.some((head) => head.active !== false), detail: `${chargeHeads.filter((head) => head.active !== false).length} active` },
    { label: "Bank / UPI", ok: !!(billingSettings.upiId || billingSettings.bankAccountNumber), detail: billingSettings.upiId || billingSettings.bankName || "Missing" },
    { label: "Watchman / staff", ok: staffUsers.some((staff) => staff.status !== "suspended"), detail: `${staffUsers.filter((staff) => staff.status !== "suspended").length} active` },
    { label: "Member app logins", ok: memberUsers.some((member) => member.status !== "suspended"), detail: `${memberUsers.filter((member) => member.status !== "suspended").length} active` },
    { label: "Billing tested", ok: bills.length > 0, detail: `${bills.length} bills` },
    { label: "Payment flow", ok: payments.length > 0, detail: `${pendingPayments.length} pending` },
    { label: "WhatsApp", ok: whatsappLogs.length > 0 && failedWhatsappCount === 0, detail: whatsappLogs.length ? `${failedWhatsappCount} failed` : "Not tested" },
    { label: "CA login", ok: auditors.some((auditor) => auditor.status !== "suspended"), detail: `${auditors.filter((auditor) => auditor.status !== "suspended").length} active` },
  ];
  const launchReadyCount = launchChecklist.filter((item) => item.ok).length;
  const qaChecklist = [
    { label: "Create visitor entry", ok: visitors.length > 0, detail: visitors.length ? `${visitors.length} visitor entries` : "Use Visitor Entry tab" },
    { label: "Approval pending shown", ok: pendingVisitors.length > 0 || visitors.some((visitor) => ["approved", "rejected", "completed"].includes(visitor.status)), detail: pendingVisitors.length ? `${pendingVisitors.length} pending` : "Create one pending visitor" },
    { label: "Video call path", ok: visitors.some((visitor) => visitor.status !== "pending") || pendingVisitors.length > 0, detail: pendingVisitors.length ? "Start call from Pending Approval" : "Test with next visitor" },
    { label: "Member mobile login", ok: memberUsers.some((member) => member.status !== "suspended"), detail: "Open /society/mobile" },
    { label: "Generate bill", ok: bills.length > 0, detail: bills.length ? `${bills.length} bills generated` : "Generate one monthly bill" },
    { label: "Send bill WhatsApp", ok: whatsappLogs.some((log) => log.messageType === "bill"), detail: whatsappLogs.some((log) => log.messageType === "bill") ? "Bill send tested" : "Use Send on a bill" },
    { label: "Submit UTR", ok: payments.length > 0, detail: payments.length ? `${payments.length} payments` : "Submit from member/mobile bill" },
    { label: "Verify payment", ok: payments.some((payment) => ["accepted", "rejected"].includes(payment.status)), detail: payments.some((payment) => payment.status === "accepted") ? "Accepted payment found" : "Accept/reject one UTR" },
    { label: "Receipt WhatsApp", ok: whatsappLogs.some((log) => log.messageType === "receipt"), detail: whatsappLogs.some((log) => log.messageType === "receipt") ? "Receipt send tested" : "Send receipt after accept" },
    { label: "Auditor export", ok: auditLogs.length > 0 || auditors.some((auditor) => auditor.status !== "suspended"), detail: auditors.length ? "Open /society/audit" : "Create CA login" },
  ];
  const qaReadyCount = qaChecklist.filter((item) => item.ok).length;
  const societyCode = user?.client_code || user?.clientCode || user?.client_id || user?.id || "";

  const saveOwner = async () => {
    try {
      setBusy(true);
      const payload = toOwnerPayload(ownerForm);
      if (editingOwner) {
        await api.patch(`/society/directory/${editingOwner.id}`, payload);
      } else {
        await api.post("/society/directory", payload);
      }
      toast.success(editingOwner ? "Owner entry updated" : "Owner entry added");
      setOwnerOpen(false);
      setEditingOwner(null);
      setOwnerForm(ownerBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save owner"));
    } finally {
      setBusy(false);
    }
  };

  const resolveNoticeExpiry = () => {
    if (noticeValidity === "infinite") return "";
    if (noticeValidity === "custom") {
      if (!noticeCustomDate) return "";
      return new Date(`${noticeCustomDate}T23:59:59`).toISOString();
    }
    const days = Number(noticeValidity);
    if (!days || days <= 0) return "";
    return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
  };

  const saveNotice = async () => {
    try {
      setBusy(true);
      const payload = { ...noticeForm, title: String(noticeForm.title || "").trim(), body: String(noticeForm.body || "").trim(), expires_at: resolveNoticeExpiry() };
      if (!payload.title) {
        toast.error("Notice title is required");
        return;
      }
      if (editingNotice) {
        await api.put(`/client/notices/${editingNotice.id}`, payload);
      } else {
        await api.post("/client/notices", payload);
      }
      toast.success(editingNotice ? "Notice updated" : "Notice added");
      setNoticeOpen(false);
      setEditingNotice(null);
      setNoticeForm(noticeBlank);
      setNoticeValidity("infinite");
      setNoticeCustomDate("");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save notice"));
    } finally {
      setBusy(false);
    }
  };

  const deleteNotice = async (notice) => {
    if (!window.confirm(`Delete ${notice.title || "this notice"}?`)) return;
    try {
      await api.delete(`/client/notices/${notice.id}`);
      toast.success("Notice deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete notice"));
    }
  };

  const deleteOwner = async (owner) => {
    if (!window.confirm(`Deactivate ${ownerLabel(owner)}?`)) return;
    try {
      await api.delete(`/society/directory/${owner.id}`);
      toast.success("Owner entry deactivated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete owner"));
    }
  };

  const bulkUpdateOwners = async () => {
    const ownerIds = selectedOwnerIds.filter((id) => owners.some((owner) => owner.id === id));
    if (!ownerIds.length) return;
    const payload = { ownerIds };
    if (bulkOwnerForm.unitType !== "unchanged") payload.unitType = bulkOwnerForm.unitType;
    if (bulkOwnerForm.occupancyStatus !== "unchanged") payload.occupancyStatus = bulkOwnerForm.occupancyStatus;
    if (bulkOwnerForm.tenantDeedStatus !== "unchanged") payload.tenantDeedStatus = bulkOwnerForm.tenantDeedStatus;
    if (bulkOwnerForm.floor.trim()) payload.floor = bulkOwnerForm.floor.trim();
    if (bulkOwnerForm.isActive !== "unchanged") payload.isActive = bulkOwnerForm.isActive === "active";
    if (Object.keys(payload).length === 1) {
      toast.error("Select at least one change to apply");
      return;
    }
    if (!window.confirm(`Apply these changes to ${ownerIds.length} selected unit${ownerIds.length === 1 ? "" : "s"}?`)) return;
    try {
      setBusy(true);
      await api.post("/society/directory/bulk-update", payload);
      toast.success(`${ownerIds.length} unit${ownerIds.length === 1 ? "" : "s"} updated`);
      setSelectedOwnerIds([]);
      setBulkOwnerForm({ unitType: "unchanged", occupancyStatus: "unchanged", tenantDeedStatus: "unchanged", floor: "", isActive: "unchanged" });
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update selected units"));
    } finally {
      setBusy(false);
    }
  };

  const oneTimeTargetOwners = oneTimeChargeForm.target === "selected"
    ? owners.filter((owner) => oneTimeOwnerIds.includes(owner.id))
    : owners.filter((owner) => oneTimeChargeForm.target === "all" || (owner.unitType || "residential") === oneTimeChargeForm.target);

  const saveOneTimeCharge = async () => {
    if (!oneTimeChargeForm.reason.trim() || Number(oneTimeChargeForm.amount) <= 0 || !oneTimeChargeForm.month) {
      toast.error("Month, description and amount are required");
      return;
    }
    if (!oneTimeTargetOwners.length) {
      toast.error("Select at least one unit");
      return;
    }
    const action = oneTimeChargeForm.adjustmentType === "credit" ? "credit" : "charge";
    if (!window.confirm(`Apply this one-time ${action} of ₹${Number(oneTimeChargeForm.amount).toLocaleString()} to ${oneTimeTargetOwners.length} unit${oneTimeTargetOwners.length === 1 ? "" : "s"} for ${oneTimeChargeForm.month}?`)) return;
    try {
      setBusy(true);
      await api.post("/society/billing/adjustments/bulk", {
        ownerIds: oneTimeTargetOwners.map((owner) => owner.id),
        applyMonth: oneTimeChargeForm.month,
        reason: oneTimeChargeForm.reason.trim(),
        amount: Number(oneTimeChargeForm.amount),
        adjustmentType: oneTimeChargeForm.adjustmentType,
      });
      toast.success(`One-time ${action} saved for ${oneTimeTargetOwners.length} units`);
      setOneTimeChargeForm((form) => ({ ...form, reason: "", amount: "" }));
      setOneTimeOwnerIds([]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save one-time charge"));
    } finally {
      setBusy(false);
    }
  };

  const cancelPendingAdjustment = async (adjustment) => {
    const reason = window.prompt(
      `Reason for cancelling this pending ${adjustment.adjustmentType === "credit" ? "credit" : "charge"} for ${adjustment.wing || ""} ${adjustment.flatNumber || ""}?`,
      "Entered incorrectly",
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Cancellation reason is required"); return; }
    try {
      setBusy(true);
      await api.delete(`/society/billing/adjustments/${adjustment.id}`, { params: { reason: reason.trim() } });
      toast.success("Pending one-time entry cancelled");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this one-time entry"));
    } finally {
      setBusy(false);
    }
  };

  const createVisitor = async () => {
    try {
      setBusy(true);
      await api.post("/society/visitors", { ...visitorForm, flatNumber: String(visitorForm.flatNumber || "").toUpperCase() });
      toast.success("Visitor entry created");
      setVisitorForm(visitorBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create visitor"));
    } finally {
      setBusy(false);
    }
  };

  const updateVisitor = async (visitor, action) => {
    try {
      await api.patch(`/society/visitors/${visitor.id}/${action}`, { remarks: "" });
      toast.success(`Visitor ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "checked out"}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update visitor"));
    }
  };

  const startCall = async () => {
    toast.info("Video calls work only in the RP Society Android app. Please use the native mobile app.");
  };

  const saveSettings = async () => {
    try {
      await api.put("/society/directory-settings", settings);
      toast.success("Directory display settings saved");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save settings"));
    }
  };

  const saveBillingSettings = async () => {
    try {
      setBusy(true);
      await api.put("/society/billing/settings", {
        ...billingSettings,
        billingDay: Number(billingSettings.billingDay) || 1,
        dueDays: Number(billingSettings.dueDays) || 15,
        paymentDueDay: Number(billingSettings.paymentDueDay) || 15,
        interestRateAnnual: Number(billingSettings.interestRateAnnual) || 0,
        nextBillNumber: Number(billingSettings.nextBillNumber) || 1,
      });
      toast.success("Billing settings saved");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save billing settings"));
    } finally {
      setBusy(false);
    }
  };

  const seedChargeHeads = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/billing/charge-heads/seed");
      toast.success(`Added ${res.data?.created || 0} state charge heads`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not add state charge heads"));
    } finally {
      setBusy(false);
    }
  };

  const addChargeHead = async () => {
    try {
      setBusy(true);
      await api.post("/society/billing/charge-heads", {
        ...chargeForm,
        unitGroup: chargeGroup,
        defaultAmount: Number(chargeForm.defaultAmount) || 0,
      });
      toast.success("Charge head added");
      setChargeForm({ ...chargeHeadBlank, unitGroup: chargeGroup });
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not add charge head"));
    } finally {
      setBusy(false);
    }
  };

  const applyChargeTemplate = (template) => {
    setChargeForm((current) => ({
      ...current,
      name: template.name,
      code: template.code,
      category: template.category,
      allocation: template.allocation,
      defaultAmount: current.defaultAmount || 0,
      active: true,
    }));
  };

  const toggleChargeHead = async (head) => {
    try {
      await api.patch(`/society/billing/charge-heads/${head.id}`, { active: !head.active });
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update charge head"));
    }
  };

  const updateChargeHeadLocal = (headId, patch) => {
    setChargeHeads((rows) => rows.map((head) => (head.id === headId ? { ...head, ...patch } : head)));
  };

  const saveChargeHead = async (head) => {
    try {
      setBusy(true);
      await api.patch(`/society/billing/charge-heads/${head.id}`, {
        name: head.name,
        code: head.code,
        category: head.category || "regular",
        allocation: head.allocation || "flat",
        unitGroup: head.unitGroup || "flat",
        applicability: head.applicability || "all",
        defaultAmount: Number(head.defaultAmount) || 0,
        active: head.active !== false,
      });
      toast.success("Charge saved");
      setEditingChargeId(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save charge"));
    } finally {
      setBusy(false);
    }
  };

  const deleteChargeHead = async (head) => {
    if (!window.confirm(`Delete ${head.name || "this charge"}? Existing generated bills will remain unchanged.`)) return;
    try {
      setBusy(true);
      await api.delete(`/society/billing/charge-heads/${head.id}`);
      if (editingChargeId === head.id) setEditingChargeId(null);
      toast.success("Charge deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete charge"));
    } finally {
      setBusy(false);
    }
  };

  const moveChargeHead = async (headId, direction) => {
    const visibleIndex = visibleChargeHeads.findIndex((head) => head.id === headId);
    const neighbor = visibleChargeHeads[visibleIndex + direction];
    if (visibleIndex < 0 || !neighbor) return;
    const from = chargeHeads.findIndex((head) => head.id === headId);
    const to = chargeHeads.findIndex((head) => head.id === neighbor.id);
    const reordered = [...chargeHeads];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    setChargeHeads(reordered);
    try {
      setBusy(true);
      await api.post("/society/billing/charge-heads/reorder", { headIds: reordered.map((head) => head.id) });
      toast.success("Charge sequence updated");
    } catch (err) {
      toast.error(formatErr(err, "Could not change charge sequence"));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const generateBills = async () => {
    if (!billingRule?.implemented) {
      toast.error(billingRule?.message || "State billing rule is not implemented");
      return;
    }
    if (flatsMissingArea > 0 && !window.confirm(`${flatsMissingArea} active flat(s) do not have Area sq ft. Per-sq-ft charges will be zero for those flats. Continue?`)) {
      return;
    }
    try {
      setBusy(true);
      const targetBillingMonth = previousMonthValue(generationMonth);
      const res = await api.post("/society/billing/generate", { month: targetBillingMonth });
      setBillingMonth(targetBillingMonth);
      toast.success(`Generated ${res.data?.generated || 0} bills`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not generate bills"));
    } finally {
      setBusy(false);
    }
  };

  const saveOpeningDues = async () => {
    try {
      setBusy(true);
      const rows = owners.filter((owner) => owner.isActive !== false).map((owner) => {
        const draft = openingDueDrafts[owner.id] || {};
        return {
          ownerId: owner.id,
          amount: Number(draft.amount || 0),
          principalAmount: draft.side === "credit" ? 0 : Number(draft.principalAmount || 0),
          interestAmount: draft.side === "credit" ? 0 : Number(draft.interestAmount || 0),
          side: draft.side === "credit" ? "credit" : "debit",
          note: draft.note || "",
          asOfDate: draft.asOfDate || "",
        };
      });
      await api.patch("/society/billing/opening-outstanding", { rows });
      toast.success("Opening balances saved");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save opening balances"));
    } finally {
      setBusy(false);
    }
  };

  const updateOpeningDueDraft = (ownerId, patch) => {
    setOpeningDueDrafts((rows) => ({ ...rows, [ownerId]: { ...(rows[ownerId] || {}), ...patch } }));
  };

  const runBillingScheduler = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/billing/scheduler/run");
      const first = res.data?.results?.[0];
      toast.success(first?.status === "skipped" ? first.reason || "Billing already generated" : "Billing scheduler completed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not run billing scheduler"));
    } finally {
      setBusy(false);
    }
  };

  const sendBillWhatsapp = async (bill) => {
    try {
      const res = await api.post(`/society/billing/bills/${bill.id}/send-whatsapp`);
      if (res.data?.ok) {
        toast.success("Bill sent on WhatsApp");
      } else {
        toast.error("WhatsApp bill failed. Check delivery log.");
      }
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not send WhatsApp bill"));
    }
  };

  const cancelBill = async (bill) => {
    const reason = window.prompt(`Reason for cancelling ${bill.billNumber || "this bill"}?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.patch(`/society/billing/bills/${bill.id}/cancel`, { reason: reason.trim() });
      toast.success("Bill cancelled");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel bill"));
    }
  };

  const deleteBill = async (bill) => {
    if (!window.confirm(`Delete bill ${bill.billNumber || ""}? Its bill accounting entry will be removed, while any payment received will remain as member credit. This cannot be undone.`)) return;
    try {
      setBusy(true);
      await api.post(`/society/billing/bills/${bill.id}/delete`, { reason: "Deleted by user from generated bills" });
      toast.success("Bill and its accounting effect deleted");
      setSelectedBillIds((ids) => ids.filter((id) => id !== bill.id));
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete bill"));
    } finally {
      setBusy(false);
    }
  };

  const bulkDeleteBills = async () => {
    const ids = selectedBillIds.filter((id) => monthBills.some((bill) => bill.id === id));
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected bill${ids.length === 1 ? "" : "s"}? All corresponding bill accounting entries will be removed and payments will remain as member credits. This cannot be undone.`)) return;
    try {
      setBusy(true);
      const res = await api.post("/society/billing/bills/delete-bulk", { billIds: ids, reason: "Bulk deleted by user from generated bills" });
      toast.success(`${res.data?.deletedCount || ids.length} bill(s) and their accounting effects deleted`);
      setSelectedBillIds([]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete selected bills"));
    } finally {
      setBusy(false);
    }
  };

  const addChargeToSelectedBills = async () => {
    const billIds = selectedBillIds.filter((id) => monthBills.some((bill) => bill.id === id));
    const name = selectedBillCharge.name.trim();
    const amount = Number(selectedBillCharge.amount);
    if (!billIds.length) return;
    if (name.length < 3 || !Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a charge description and amount greater than zero");
      return;
    }
    if (!window.confirm(`Add ${name} of ₹${amount.toLocaleString()} to ${billIds.length} selected bill${billIds.length === 1 ? "" : "s"}? Only these selected bills will change.`)) return;
    try {
      setBusy(true);
      const res = await api.post("/society/billing/bills/add-line-bulk", {
        billIds,
        name,
        amount,
        reason: selectedBillCharge.reason.trim() || `Committee-approved bulk charge: ${name}`,
      });
      toast.success(`${res.data?.updatedCount || billIds.length} selected bill(s) updated and reposted`);
      setSelectedBillCharge({ name: "", amount: "", reason: "" });
      setSelectedBillIds([]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not add charge to selected bills"));
    } finally {
      setBusy(false);
    }
  };

  // Accounting Phase 6: Billing<->Accounting sync-status badge, reused for
  // both bills (billingAccountingStatus) and payments (paymentAccountingStatus).
  // Additive/read-only display - never mutates anything itself.
  const accountingStatusBadge = (status) => {
    const map = {
      posted: "bg-emerald-50 text-emerald-700",
      error: "bg-red-50 text-red-700",
      reversed: "bg-gray-100 text-gray-600",
      not_required: "bg-gray-100 text-gray-500",
    };
    const label = {
      posted: "Posted",
      error: "Error",
      reversed: "Reversed",
      not_required: "Not required",
    }[status] || "Pending";
    return <span className={`inline-flex rounded-sm px-2 py-1 text-xs font-bold ${map[status] || "bg-amber-50 text-amber-700"}`}>{label}</span>;
  };

  const retryBillAccounting = async (bill) => {
    try {
      await api.post(`/society/billing/bills/${bill.id}/accounting/retry`);
      toast.success("Accounting posting retried");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this bill to Accounting"));
    }
  };

  const retryPaymentAccounting = async (payment) => {
    try {
      await api.post(`/society/billing/payments/${payment.id}/accounting/retry`);
      toast.success("Accounting posting retried");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this payment to Accounting"));
    }
  };

  const orderBillLines = (lines) => [...lines].sort((a, b) => {
    const rank = (line) => {
      if (line.category === "opening" || line.code === "OPENING") return 0;
      if (line.category === "opening_credit" || ["OPENING-CREDIT", "ADVANCE-APPLIED"].includes(line.code)) return 3;
      if (line.category === "adjustment" || line.adjustmentId || String(line.code || "").startsWith("ADJ-")) return 2;
      return 1;
    };
    return rank(a) - rank(b);
  });

  const openBillEditor = async (bill) => {
    try {
      setBusy(true);
      const res = await api.get(`/society/billing/bills/${bill.id}`);
      const fullBill = res.data || bill;
      setEditingBill(fullBill);
      setBillLineDrafts(orderBillLines((fullBill.lineItems || []).map((item, index) => ({
        id: item.id || item.adjustmentId || item.headId || `line-${index}`,
        code: item.code || "",
        name: item.name || "",
        category: item.category || "regular",
        allocation: item.allocation || "manual",
        rate: item.rate ?? item.amount ?? 0,
        quantity: item.quantity ?? 1,
        amount: item.amount ?? 0,
        headId: item.headId || "",
        adjustmentId: item.adjustmentId || "",
      }))));
      setBillInterestDraft(Number(fullBill.interest || 0));
      setBillEditReason("");
      setBillEditorOpen(true);
    } catch (err) {
      toast.error(formatErr(err, "Could not open bill"));
    } finally {
      setBusy(false);
    }
  };

  const updateBillLineDraft = (lineId, patch) => {
    setBillLineDrafts((rows) => orderBillLines(rows.map((row) => (row.id === lineId ? { ...row, ...patch } : row))));
  };

  const addBillLineDraft = () => {
    setBillLineDrafts((rows) => orderBillLines([
      ...rows,
      {
        id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        code: "MANUAL",
        name: "",
        category: "adjustment",
        allocation: "manual",
        rate: "",
        quantity: 1,
        amount: "",
        headId: "",
        adjustmentId: "",
      },
    ]));
  };

  const removeBillLineDraft = (lineId) => {
    setBillLineDrafts((rows) => rows.filter((row) => row.id !== lineId));
  };

  const saveBillLines = async () => {
    if (!editingBill) return;
    const cleanLines = orderBillLines(billLineDrafts.map((line) => ({
      code: line.code || "",
      name: String(line.name || "").trim(),
      category: line.category || "regular",
      allocation: line.allocation || "manual",
      rate: Number(line.rate || line.amount || 0),
      quantity: Number(line.quantity || 1),
      amount: Number(line.amount || 0),
      headId: line.headId || "",
      adjustmentId: line.adjustmentId || "",
    })));
    const invalidLine = cleanLines.find((line) => (
      !line.name
      || !Number.isFinite(line.amount)
      || (line.amount < 0 && !["opening_credit", "adjustment"].includes(line.category))
    ));
    if (invalidLine) {
      toast.error("Every line needs a title and valid amount");
      return;
    }
    try {
      setBusy(true);
      const interest = Number(billInterestDraft);
      if (!Number.isFinite(interest) || interest < 0) {
        toast.error("Interest charged must be zero or more");
        return;
      }
      await api.patch(`/society/billing/bills/${editingBill.id}/lines`, { lineItems: cleanLines, interest, reason: billEditReason });
      toast.success("Bill updated");
      setBillEditorOpen(false);
      setEditingBill(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save bill"));
    } finally {
      setBusy(false);
    }
  };

  const downloadBillPdf = async (bill) => {
    try {
      const res = await api.get(`/society/billing/bills/${bill.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const unitNumber = [bill.wing, bill.flatNumber]
        .filter(Boolean)
        .map((part) => String(part).replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, ""))
        .filter(Boolean)
        .join("-");
      const billNumber = String(bill.billNumber || "society-bill").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = unitNumber ? `Flat-${unitNumber}_Bill-${billNumber}.pdf` : `Bill-${billNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download bill PDF"));
    }
  };

  const downloadMonthlyBillsPdf = async () => {
    try {
      setBusy(true);
      const res = await api.get("/society/billing/monthly-bills-pdf", { params: { month: billingMonth }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `society-bills-${billingMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download monthly bills PDF"));
    } finally {
      setBusy(false);
    }
  };

  const verifyPayment = async (payment, accepted) => {
    try {
      await api.patch(`/society/billing/payments/${payment.id}/verify`, { accepted, remarks: "" });
      toast.success(accepted ? "Payment accepted" : "Payment rejected");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not verify payment"));
    }
  };

  const bulkVerifyPayments = async (accepted) => {
    const selected = payments.filter((payment) => selectedPaymentIds.includes(payment.id) && payment.status === "pending_verification");
    if (!selected.length) return;
    if (!window.confirm(`${accepted ? "Accept" : "Reject"} ${selected.length} selected pending payment${selected.length === 1 ? "" : "s"}?`)) return;
    try {
      setBusy(true);
      await Promise.all(selected.map((payment) => api.patch(`/society/billing/payments/${payment.id}/verify`, { accepted, remarks: "Bulk verified from UTR Payments" })));
      toast.success(`${selected.length} payment${selected.length === 1 ? "" : "s"} ${accepted ? "accepted" : "rejected"}`);
      setSelectedPaymentIds([]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not verify selected payments"));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const deletePayment = async (payment) => {
    const reference = payment.receiptNumber || payment.utr || payment.billNumber || "this payment";
    if (!window.confirm(`Delete test payment ${reference}? An accepted payment will also be removed from its bill balance.`)) return;
    try {
      setBusy(true);
      await api.delete(`/society/billing/payments/${payment.id}`, { params: { reason: "Test payment removed from UTR verification" } });
      toast.success("Test payment deleted and bill balance recalculated");
      setSelectedPaymentIds((ids) => ids.filter((id) => id !== payment.id));
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not safely delete payment"));
    } finally {
      setBusy(false);
    }
  };

  const downloadReceiptPdf = async (payment) => {
    try {
      const res = await api.get(`/society/billing/payments/${payment.id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${payment.receiptNumber || "society-receipt"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download receipt"));
    }
  };

  const sendReceiptWhatsapp = async (payment) => {
    try {
      const res = await api.post(`/society/billing/payments/${payment.id}/send-receipt-whatsapp`);
      if (res.data?.ok) {
        toast.success("Receipt sent on WhatsApp");
      } else {
        toast.error("WhatsApp receipt failed. Check delivery log.");
      }
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not send receipt"));
    }
  };

  const retryWhatsappLog = async (log) => {
    try {
      const res = await api.post("/society/whatsapp/retry", { logId: log.id });
      if (res.data?.ok) {
        toast.success("WhatsApp retry completed");
      } else {
        toast.error("WhatsApp retry failed");
      }
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not retry WhatsApp"));
    }
  };

  const closeFinancialPeriod = async () => {
    try {
      setBusy(true);
      await api.post("/society/financial-closes", financialCloseForm);
      toast.success("Financial period closed");
      setFinancialCloseForm(financialCloseBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not close financial period"));
    } finally {
      setBusy(false);
    }
  };

  const reopenFinancialPeriod = async (row) => {
    const reason = window.prompt(`Reason for reopening ${row.fromDate} to ${row.toDate}?`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.patch(`/society/financial-closes/${row.id}/reopen`, { reason: reason.trim() });
      toast.success("Financial period reopened");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not reopen period"));
    }
  };

  const downloadAuditReport = async (report) => {
    try {
      const res = await api.get("/society/audit/export", { params: { report }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `society-${report}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Could not download report"));
    }
  };

  const importBankStatement = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/bank-statements/import", { csvText: bankCsvText, account: "Main bank" });
      if (bankCsvFile) {
        await uploadAttachment("bank_statement", res.data?.importId || "", bankCsvFile, "Bank statement CSV");
      }
      toast.success(`Imported ${res.data?.imported || 0}. Matched ${res.data?.matched || 0}, unmatched ${res.data?.unmatched || 0}`);
      setBankCsvText("");
      setBankCsvFile(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not import bank statement"));
    } finally {
      setBusy(false);
    }
  };

  const reconcileBankStatement = async () => {
    try {
      setBusy(true);
      const res = await api.post("/society/bank-statements/reconcile");
      toast.success(`Checked ${res.data?.checked || 0}. Matched ${res.data?.matched || 0}, unmatched ${res.data?.unmatched || 0}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not reconcile bank statement"));
    } finally {
      setBusy(false);
    }
  };

  const readBankCsvFile = (file) => {
    if (!file) return;
    setBankCsvFile(file);
    const reader = new FileReader();
    reader.onload = () => setBankCsvText(String(reader.result || ""));
    reader.onerror = () => toast.error("Could not read CSV file");
    reader.readAsText(file);
  };

  const uploadAttachment = async (entityType, entityId, file, label = "") => {
    if (!file) return;
    try {
      setBusy(true);
      const formData = new FormData();
      formData.append("entityType", entityType);
      formData.append("entityId", entityId || "");
      formData.append("label", label || file.name || "Attachment");
      formData.append("file", file);
      await api.post("/society/attachments", formData);
      toast.success("Attachment uploaded");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not upload attachment"));
    } finally {
      setBusy(false);
    }
  };

  const uploadOwnerPhoto = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/society/owner-photo/upload", formData);
      setOwnerForm((f) => ({ ...f, ownerPhoto: res.data?.url || "" }));
      toast.success("Owner photo attached");
    } catch (err) {
      toast.error(formatErr(err, "Could not attach owner photo"));
    } finally {
      setBusy(false);
    }
  };

  const openAttachment = async (attachment) => {
    try {
      const res = await api.get(`/society/attachments/${attachment.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatErr(err, "Could not open attachment"));
    }
  };

  const attachmentCount = (entityType, entityId) => attachments.filter((item) => item.entityType === entityType && item.entityId === entityId).length;

  const cleanLoginPayload = (form, includePassword = true) => {
    const payload = {
      ...form,
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || "",
    };
    if (!includePassword || !payload.password) delete payload.password;
    return payload;
  };

  const addAuditor = async () => {
    try {
      setBusy(true);
      await api.post("/society/auditors", auditorForm);
      toast.success("Auditor login created");
      setAuditorForm(auditorBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create auditor"));
    } finally {
      setBusy(false);
    }
  };

  const addStaffUser = async () => {
    try {
      setBusy(true);
      await api.post("/society/staff", cleanLoginPayload(staffForm));
      toast.success("Society staff login created");
      setStaffForm(staffBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create staff login"));
    } finally {
      setBusy(false);
    }
  };

  const addMemberUser = async () => {
    try {
      setBusy(true);
      await api.post("/society/members", cleanLoginPayload(memberForm));
      toast.success("Member app login created");
      setMemberForm(memberBlank);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create member login"));
    } finally {
      setBusy(false);
    }
  };

  const editMemberUser = (member) => {
    setEditingMemberLogin(member);
    setMemberForm({
      ownerId: member.ownerId || "",
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || member.ownerMobile || "",
      password: "",
      societyRole: member.societyRole || "flat_owner",
      status: member.status || "active",
    });
  };

  const cancelMemberEdit = () => {
    setEditingMemberLogin(null);
    setMemberForm(memberBlank);
  };

  const saveMemberUser = async () => {
    if (!editingMemberLogin) return addMemberUser();
    try {
      setBusy(true);
      await api.patch(`/society/members/${editingMemberLogin.id}`, cleanLoginPayload(memberForm, false));
      toast.success("Owner login updated");
      cancelMemberEdit();
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update owner login"));
    } finally {
      setBusy(false);
    }
  };

  const editStaffUser = (staff) => {
    setEditingStaffLogin(staff);
    setStaffForm({
      name: staff.name || "",
      email: staff.email || "",
      phone: staff.phone || "",
      password: "",
      societyRole: staff.societyRole || "security",
      status: staff.status || "active",
    });
  };

  const cancelStaffEdit = () => {
    setEditingStaffLogin(null);
    setStaffForm(staffBlank);
  };

  const saveStaffUser = async () => {
    if (!editingStaffLogin) return addStaffUser();
    try {
      setBusy(true);
      await api.patch(`/society/staff/${editingStaffLogin.id}`, cleanLoginPayload(staffForm, false));
      toast.success("Staff login updated");
      cancelStaffEdit();
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update staff login"));
    } finally {
      setBusy(false);
    }
  };

  const suspendMemberUser = async (member) => {
    try {
      await api.delete(`/society/members/${member.id}`);
      toast.success("Member login suspended");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update member login"));
    }
  };

  const activateMemberUser = async (member) => {
    try {
      await api.patch(`/society/members/${member.id}`, { status: "active" });
      toast.success("Owner login activated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not activate owner login"));
    }
  };

  const deleteMemberUser = async (member) => {
    if (!window.confirm(`Delete owner login for ${member.name || "this owner"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/members/${member.id}?hard=true`);
      if (editingMemberLogin?.id === member.id) cancelMemberEdit();
      toast.success("Owner login deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete owner login"));
    }
  };

  const suspendStaffUser = async (staff) => {
    try {
      await api.delete(`/society/staff/${staff.id}`);
      toast.success("Staff login suspended");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update staff login"));
    }
  };

  const activateStaffUser = async (staff) => {
    try {
      await api.patch(`/society/staff/${staff.id}`, { status: "active" });
      toast.success("Staff login activated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not activate staff login"));
    }
  };

  const deleteStaffUser = async (staff) => {
    if (!window.confirm(`Delete staff login for ${staff.name || "this staff member"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/staff/${staff.id}?hard=true`);
      if (editingStaffLogin?.id === staff.id) cancelStaffEdit();
      toast.success("Staff login deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete staff login"));
    }
  };

  const resetMemberPassword = async (member) => {
    const password = window.prompt(`Set new password for ${member.name || "member"}`);
    if (!password) return;
    if (password.length < 6) {
      toast.error("Password should be at least 6 characters");
      return;
    }
    try {
      setBusy(true);
      await api.patch(`/society/members/${member.id}`, { password });
      toast.success("Member password updated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update member password"));
    } finally {
      setBusy(false);
    }
  };

  const resetStaffPassword = async (staff) => {
    const password = window.prompt(`Set new password for ${staff.name || "staff"}`);
    if (!password) return;
    if (password.length < 6) {
      toast.error("Password should be at least 6 characters");
      return;
    }
    try {
      setBusy(true);
      await api.patch(`/society/staff/${staff.id}`, { password });
      toast.success("Staff password updated");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update staff password"));
    } finally {
      setBusy(false);
    }
  };

  const suspendAuditor = async (auditor) => {
    try {
      await api.delete(`/society/auditors/${auditor.id}`);
      toast.success("Auditor suspended");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update auditor"));
    }
  };

  const importDirectory = async () => {
    try {
      const res = await api.post("/society/directory/import", { csvText });
      toast.success(`Imported ${res.data?.imported || 0} entries`);
      setImportOpen(false);
      setCsvText("");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Import failed"));
    }
  };

  const exportDirectory = async () => {
    try {
      const res = await api.get("/society/directory/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "society-directory.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err, "Export failed"));
    }
  };

  const displayOwner = async () => {
    try {
      const res = await api.post("/society/directory/display-owner", displayForm);
      toast.success(res.data?.message || "Display prompt sent to device");
    } catch (err) {
      toast.error(formatErr(err, "Could not display owner"));
    }
  };

  const speakAnswer = useCallback((answer) => {
    if (!answer) return false;
    if (typeof window === "undefined" || !window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") {
      setTtsStatus("Voice output is not supported in this browser.");
      toast.error("Voice output is not supported in this browser.");
      return false;
    }
    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      synth.resume?.();
      const utter = new SpeechSynthesisUtterance(answer);
      const voices = synth.getVoices?.() || [];
      const preferredVoice = voices.find((voice) => /en[-_]IN/i.test(voice.lang)) || voices.find((voice) => /^en/i.test(voice.lang)) || voices[0];
      if (preferredVoice) utter.voice = preferredVoice;
      utter.lang = "en-IN";
      utter.rate = 0.95;
      utter.pitch = 1;
      utter.volume = 1;
      let keepAlive;
      utter.onstart = () => {
        setTtsStatus("Speaking answer...");
        keepAlive = window.setInterval(() => synth.resume?.(), 1000);
      };
      utter.onend = () => {
        if (keepAlive) window.clearInterval(keepAlive);
        setTtsStatus("Voice answer completed.");
      };
      utter.onerror = (event) => {
        if (keepAlive) window.clearInterval(keepAlive);
        const reason = event?.error ? `Voice blocked: ${event.error}` : "Voice output failed.";
        setTtsStatus(reason);
        toast.error(reason);
      };
      const speakNow = () => {
        synth.cancel();
        synth.resume?.();
        synth.speak(utter);
      };
      if (!voices.length && "onvoiceschanged" in synth) {
        synth.onvoiceschanged = speakNow;
        window.setTimeout(speakNow, 250);
      } else {
        speakNow();
      }
      return true;
    } catch (err) {
      setTtsStatus("Voice output failed. Please check browser sound permission.");
      toast.error("Voice output failed. Please check browser sound permission.");
      return false;
    }
  }, []);

  const runVoiceSearch = async (transcriptOverride = "") => {
    try {
      const transcript = transcriptOverride || voiceText;
      const res = await api.post("/housing-society/agent/query", {
        text: transcript,
        deviceId: displayForm.deviceId,
        language: "auto",
        context: { source: "client_panel" },
        durationSeconds: displayForm.durationSeconds,
      });
      const answer = res.data?.speechText || res.data?.answer || "Voice command processed.";
      if (process.env.NODE_ENV === "development") {
        console.debug("[SocietyVoiceAgent]", {
          raw: transcript,
          normalized: res.data?.debug?.normalized || res.data?.normalizedText,
          intent: res.data?.intent,
          confidence: res.data?.confidence,
          action: res.data?.action,
          matches: res.data?.debug?.matches,
          answer,
        });
      }
      setAgentAnswer(answer);
      setAgentDisplay(res.data?.display || null);
      if (!speakAnswer(answer)) {
        setTtsStatus("Text answer ready. Voice could not start.");
      }
      toast.success(answer);
    } catch (err) {
      toast.error(formatErr(err, "Voice search failed"));
    }
  };

  const startPcMicAgent = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      toast.error("Speech recognition is not supported in this browser. Use Chrome or type the command.");
      return;
    }
    try {
      const recognition = new Recognition();
      recognition.lang = "en-IN";
      recognition.interimResults = false;
      recognition.continuous = false;
      recognition.onstart = () => setListening(true);
      recognition.onerror = () => {
        setListening(false);
        toast.error("Could not hear the command. Please try again.");
      };
      recognition.onend = () => setListening(false);
      recognition.onresult = (event) => {
        const transcript = Array.from(event.results || [])
          .map((result) => result?.[0]?.transcript || "")
          .join(" ")
          .trim();
        if (!transcript) return;
        setVoiceText(transcript);
        const cleaned = transcript.replace(/^\s*hello\s+rp[:,\s-]*/i, "").trim() || transcript;
        runVoiceSearch(cleaned);
      };
      recognition.start();
    } catch (err) {
      setListening(false);
      toast.error("Microphone could not start.");
    }
  };

  const approvalLink = (visitor) => {
    const frontendBase = window.location.origin;
    return `${frontendBase}/society/approve/${visitor.id}?token=${encodeURIComponent(visitor.approvalToken || "")}`;
  };

  const copyLaunchLinks = async () => {
    const origin = window.location.origin;
    const text = [
      `Society admin panel: ${origin}/client/society`,
      `Watchman / guard screen: ${origin}/society/guard`,
      `Resident mobile app: ${origin}/society/mobile`,
      `CA audit login: ${origin}/society/audit`,
    ].join("\n");
    try {
      await navigator.clipboard?.writeText(text);
      toast.success("Launch links copied");
    } catch {
      toast.error("Could not copy launch links");
    }
  };

  const openLaunchLink = (path) => {
    const token = sessionStorage.getItem("access_token") || "";
    const target = token
      ? `/switch-login?token=${encodeURIComponent(token)}&to=${encodeURIComponent(path)}`
      : path;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      {setupMode && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-extrabold">Setup mode</div>
          <div className="mt-1">You can prepare owners, member credentials, opening balances and billing settings. Live bills, notices, payments, owner access and automatic services remain unavailable until admin activates the per-flat subscription.</div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Directory Entries" value={owners.length} />
        <StatCard label="Pending Visitors" value={pendingVisitors.length} />
        <StatCard label="Wings" value={Math.max(0, wings.length - 1)} />
        <StatCard label="Devices" value={devices.length} />
      </div>
      <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2 text-xs text-[#6B7280]">
        API target: <span className="font-mono text-[#111827]">{apiDebugTarget()}</span>
      </div>

      <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-[#111827]">Launch readiness</h2>
            <div className="mt-1 text-sm text-[#6B7280]">{launchReadyCount} of {launchChecklist.length} checks ready for tomorrow launch</div>
          </div>
          <div className={`rounded-sm px-3 py-2 text-sm font-extrabold ${launchReadyCount === launchChecklist.length ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {launchReadyCount === launchChecklist.length ? "Ready" : "Setup pending"}
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {launchChecklist.map((item) => (
            <button key={item.label} type="button" onClick={() => {
              if (["Member app logins", "Watchman / staff"].includes(item.label)) selectSocietyTab("logins");
              else if (item.label.includes("Billing") || item.label.includes("Charge") || item.label.includes("Bank") || item.label.includes("WhatsApp") || item.label.includes("Payment")) selectSocietyTab("billing");
              else if (item.label.includes("Owner")) selectSocietyTab("directory");
            }} className={`rounded-sm border p-3 text-left ${item.ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-bold text-[#111827]">{item.label}</div>
                <span className={`rounded-sm px-2 py-0.5 text-xs font-black ${item.ok ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>{item.ok ? "OK" : "TODO"}</span>
              </div>
              <div className="mt-1 text-xs text-[#6B7280]">{item.detail}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-[#111827]">Launch QA test run</h2>
            <div className="mt-1 text-sm text-[#6B7280]">{qaReadyCount} of {qaChecklist.length} flow tests completed</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => openLaunchLink("/society/guard")}>Guard screen</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => openLaunchLink("/society/mobile")}>Mobile app</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => openLaunchLink("/society/audit")}>CA audit</Button>
            <Button type="button" variant="outline" size="sm" onClick={copyLaunchLinks}>Copy links</Button>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {qaChecklist.map((item) => (
            <div key={item.label} className={`rounded-sm border p-3 ${item.ok ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-bold text-[#111827]">{item.label}</div>
                <span className={`rounded-sm px-2 py-0.5 text-xs font-black ${item.ok ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"}`}>{item.ok ? "OK" : "TEST"}</span>
              </div>
              <div className="mt-1 text-xs text-[#6B7280]">{item.detail}</div>
            </div>
          ))}
        </div>
      </div>

      <Tabs value={activeSocietyTab} onValueChange={selectSocietyTab} className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-1">
          <TabsTrigger value="directory">Owner Directory</TabsTrigger>
          <TabsTrigger value="logins">App Logins</TabsTrigger>
          <TabsTrigger value="notices">Notices & Alerts</TabsTrigger>
          <TabsTrigger value="visitor">Visitor Entry</TabsTrigger>
          <TabsTrigger value="pending">Pending Approval</TabsTrigger>
          <TabsTrigger value="history">Visitor History</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="display">Display Settings</TabsTrigger>
          <TabsTrigger value="voice">Voice / Display Test</TabsTrigger>
        </TabsList>

        <TabsContent value="directory" className="mt-5 space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_180px]">
              <Field label="Search owner / flat / mobile">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search owner, flat, mobile, wing" />
                </div>
              </Field>
              <Field label="Wing">
                <Select value={wingFilter} onValueChange={setWingFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{wings.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setImportOpen(true)}><Upload className="mr-2 h-4 w-4" /> Import</Button>
              <Button variant="outline" onClick={exportDirectory}><Download className="mr-2 h-4 w-4" /> Export</Button>
              <Button onClick={() => { setEditingOwner(null); setOwnerForm(ownerBlank); setOwnerOpen(true); }} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Add Owner</Button>
            </div>
          </div>

          {selectedOwnerIds.length > 0 && (
            <div className="rounded-sm border border-blue-200 bg-blue-50 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="font-bold text-blue-950">{selectedOwnerIds.length} unit{selectedOwnerIds.length === 1 ? "" : "s"} selected</div>
                <Button type="button" variant="outline" size="sm" onClick={() => setSelectedOwnerIds([])}>Clear selection</Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <Field label="Unit type">
                  <Select value={bulkOwnerForm.unitType} onValueChange={(unitType) => setBulkOwnerForm((form) => ({ ...form, unitType }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="unchanged">No change</SelectItem><SelectItem value="residential">Residential flat</SelectItem><SelectItem value="shop">Shop</SelectItem><SelectItem value="office">Office</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Occupancy">
                  <Select value={bulkOwnerForm.occupancyStatus} onValueChange={(occupancyStatus) => setBulkOwnerForm((form) => ({ ...form, occupancyStatus }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="unchanged">No change</SelectItem><SelectItem value="owner_occupied">Owner occupied</SelectItem><SelectItem value="tenant_occupied">Tenant occupied</SelectItem><SelectItem value="vacant">Vacant</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Tenant deed">
                  <Select value={bulkOwnerForm.tenantDeedStatus} onValueChange={(tenantDeedStatus) => setBulkOwnerForm((form) => ({ ...form, tenantDeedStatus }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="unchanged">No change</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="submitted">Submitted</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Floor (optional)"><Input value={bulkOwnerForm.floor} onChange={(e) => setBulkOwnerForm((form) => ({ ...form, floor: e.target.value }))} placeholder="No change" /></Field>
                <Field label="Status">
                  <Select value={bulkOwnerForm.isActive} onValueChange={(isActive) => setBulkOwnerForm((form) => ({ ...form, isActive }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="unchanged">No change</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </Field>
                <div className="flex items-end"><Button disabled={busy} onClick={bulkUpdateOwners} className="w-full bg-[#111827] text-white hover:bg-[#374151]">Apply to selected</Button></div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"><input type="checkbox" aria-label="Select all visible owners" checked={filteredOwners.length > 0 && filteredOwners.every((owner) => selectedOwnerIds.includes(owner.id))} onChange={(e) => setSelectedOwnerIds(e.target.checked ? Array.from(new Set([...selectedOwnerIds, ...filteredOwners.map((owner) => owner.id)])) : selectedOwnerIds.filter((id) => !filteredOwners.some((owner) => owner.id === id)))} /></TableHead>
                  <TableHead>Flat</TableHead>
                  <TableHead>Unit type</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Committee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOwners.map((owner) => (
                  <TableRow key={owner.id}>
                    <TableCell><input type="checkbox" aria-label={`Select ${owner.wing} ${owner.flatNumber}`} checked={selectedOwnerIds.includes(owner.id)} onChange={(e) => setSelectedOwnerIds((ids) => e.target.checked ? Array.from(new Set([...ids, owner.id])) : ids.filter((id) => id !== owner.id))} /></TableCell>
                    <TableCell className="font-semibold">{owner.wing} {owner.flatNumber}</TableCell>
                    <TableCell>{({ residential: "Residential", shop: "Shop", office: "Office", other: "Other" })[owner.unitType || "residential"]}</TableCell>
                    <TableCell>{Number(owner.areaSqFt || 0) ? `${Number(owner.areaSqFt || 0).toLocaleString()} sq ft` : "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {owner.ownerPhoto ? <img src={resolveMediaUrl(owner.ownerPhoto)} alt="" className="h-9 w-9 rounded-sm object-cover" /> : <div className="h-9 w-9 rounded-sm bg-[#EEF2F7]" />}
                        <div>
                          <div className="font-semibold">{owner.ownerName}</div>
                          <div className="text-xs text-[#6B7280]">{owner.ownerEmail || owner.intercomNumber || "No email/intercom"}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{owner.ownerMobile}</TableCell>
                    <TableCell>
                      <div>{owner.tenantName || "-"}</div>
                      {owner.occupancyStatus === "tenant_occupied" && (
                        <div className={`mt-0.5 text-xs ${owner.tenantDeedStatus === "submitted" ? "text-emerald-700" : "text-amber-700"}`}>
                          Deed {owner.tenantDeedStatus === "submitted" ? "submitted" : "pending"}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>{owner.vehicleNumber || owner.parkingNumber || "-"}</div>
                      {Array.isArray(owner.vehicles) && owner.vehicles.length > 0 && (
                        <div className="mt-0.5 text-xs text-[#6B7280]">
                          {owner.vehicles.map((v) => v.number || (v.nonRtoEv ? "EV scooter" : v.type === "4w" ? "4W" : "2W")).join(", ")}
                        </div>
                      )}
                      {(Number(owner.vehicle2wCount || 0) + Number(owner.vehicle4wCount || 0) + Number(owner.vehicleCycleCount || 0)) > 0 && (
                        <div className="text-xs text-[#9CA3AF]">2W:{Number(owner.vehicle2wCount || 0)} 4W:{Number(owner.vehicle4wCount || 0)} Cy:{Number(owner.vehicleCycleCount || 0)}</div>
                      )}
                    </TableCell>
                    <TableCell>{owner.committeeTitle || committeeRoles.find((r) => r.value === owner.committeeRole)?.label || "-"}</TableCell>
                    <TableCell>{owner.isActive === false ? "Inactive" : "Active"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setEditingOwner(owner); setOwnerForm(ownerToForm(owner)); setOwnerOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => deleteOwner(owner)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredOwners.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-[#6B7280]">No directory entries found.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="logins" className="mt-5 space-y-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
            <Input value={loginSearch} onChange={(e) => setLoginSearch(e.target.value)} className="pl-9" placeholder="Search owner, flat, staff, mobile, role or status" />
          </div>
          <div className="rounded-sm border border-[#BFDBFE] bg-[#EFF6FF] p-4">
            <h3 className="text-lg font-extrabold text-[#111827]">Mobile app login setup</h3>
            <div className="mt-1 text-sm text-[#4B5563]">
              Create passwords here for flat owners, committee members, guards and office staff.
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[220px_1fr]">
              <div className="rounded-sm border border-blue-200 bg-white px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6B7280]">Society code</div>
                <div className="mt-1 text-xl font-extrabold tracking-wide text-[#111827]">{societyCode || "Not available"}</div>
              </div>
              <div className="flex items-center rounded-sm border border-blue-200 bg-white px-3 py-2 text-sm text-[#374151]">
                In RP Society app, users enter this Society Code, then their login mobile or optional email and the password set below.
              </div>
            </div>
          </div>

          {user?.role === "society_staff" ? (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
              Only client admin can create or reset app logins.
            </div>
          ) : (
            <div className="grid gap-5 xl:grid-cols-2">
            <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <div className="mb-4">
                  <h3 className="text-xl font-extrabold text-[#111827]">Flat owner / committee login</h3>
                  <div className="mt-1 text-sm text-[#6B7280]">For flat owners. Select Chairman, Secretary, Treasurer or Committee Member if this owner has that role.</div>
                </div>
                <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 md:grid-cols-2">
                  <Field label="Select flat owner">
                    <Select value={memberForm.ownerId || "none"} onValueChange={(v) => {
                      const owner = owners.find((item) => item.id === v);
                      setMemberForm((f) => ({
                        ...f,
                        ownerId: v === "none" ? "" : v,
                        name: owner?.ownerName || f.name,
                        email: owner?.ownerEmail || f.email,
                        phone: owner?.ownerMobile || f.phone,
                        societyRole: owner?.committeeRole || f.societyRole || "flat_owner",
                      }));
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select flat</SelectItem>
                        {owners.map((owner) => <SelectItem key={owner.id} value={owner.id}>{ownerLabel(owner)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Owner name"><Input value={memberForm.name} onChange={(e) => setMemberForm((f) => ({ ...f, name: e.target.value }))} /></Field>
                  <Field label="Login mobile"><Input value={memberForm.phone} onChange={(e) => setMemberForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label="Login email (optional)"><Input type="email" value={memberForm.email} onChange={(e) => setMemberForm((f) => ({ ...f, email: e.target.value }))} /></Field>
                  <Field label="Member role">
                    <Select value={memberForm.societyRole || "flat_owner"} onValueChange={(v) => setMemberForm((f) => ({ ...f, societyRole: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat_owner">Flat Owner</SelectItem>
                        <SelectItem value="chairman">Chairman</SelectItem>
                        <SelectItem value="secretary">Secretary</SelectItem>
                        <SelectItem value="treasurer">Treasurer</SelectItem>
                        <SelectItem value="committee_member">Committee member</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={editingMemberLogin ? "New password (optional)" : "Set password"}><Input type="password" value={memberForm.password} onChange={(e) => setMemberForm((f) => ({ ...f, password: e.target.value }))} placeholder={editingMemberLogin ? "Leave blank to keep current password" : "Minimum 6 characters"} /></Field>
                  <div className="flex items-end gap-2">
                    <Button disabled={busy || !memberForm.ownerId || (!editingMemberLogin && !memberForm.password) || (!memberForm.email && !memberForm.phone)} onClick={saveMemberUser} className="w-full bg-[#111827] text-white hover:bg-[#374151]">
                      {editingMemberLogin ? <Pencil className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />} {editingMemberLogin ? "Update owner login" : "Create owner login"}
                    </Button>
                    {editingMemberLogin ? <Button variant="outline" onClick={cancelMemberEdit}>Cancel</Button> : null}
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Owner</TableHead><TableHead>Flat</TableHead><TableHead>Role</TableHead><TableHead>Mobile</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredMemberUsers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-semibold">{member.name}</TableCell>
                          <TableCell>{member.wing} {member.flatNumber}</TableCell>
                          <TableCell>{societyMemberRoleLabels[member.societyRole] || member.societyRole || "Flat Owner"}</TableCell>
                          <TableCell>{member.phone || member.ownerMobile || "-"}</TableCell>
                          <TableCell>{member.status || "active"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => editMemberUser(member)}>Edit</Button>
                              <Button variant="outline" size="sm" onClick={() => resetMemberPassword(member)}>Password</Button>
                              {member.status === "suspended" ? <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => activateMemberUser(member)}>Activate</Button> : <Button variant="outline" size="sm" onClick={() => suspendMemberUser(member)}>Suspend</Button>}
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteMemberUser(member)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {memberUsers.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No owner logins created yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <div className="mb-4">
                  <h3 className="text-xl font-extrabold text-[#111827]">Guard / office staff login</h3>
                  <div className="mt-1 text-sm text-[#6B7280]">For non-flat-owner users like guards and society office staff.</div>
                </div>
                <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 md:grid-cols-2">
                  <Field label="Name"><Input value={staffForm.name} onChange={(e) => setStaffForm((f) => ({ ...f, name: e.target.value }))} /></Field>
                  <Field label="Login email (optional)"><Input type="email" value={staffForm.email} onChange={(e) => setStaffForm((f) => ({ ...f, email: e.target.value }))} /></Field>
                  <Field label="Login mobile"><Input value={staffForm.phone} onChange={(e) => setStaffForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label={editingStaffLogin ? "New password (optional)" : "Set password"}><Input type="password" value={staffForm.password} onChange={(e) => setStaffForm((f) => ({ ...f, password: e.target.value }))} placeholder={editingStaffLogin ? "Leave blank to keep current password" : "Minimum 6 characters"} /></Field>
                  <Field label="Role">
                    <Select value={staffForm.societyRole} onValueChange={(v) => setStaffForm((f) => ({ ...f, societyRole: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="security">Guard / Security</SelectItem>
                        <SelectItem value="office_staff">Office Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="flex items-end gap-2">
                    <Button disabled={busy || !staffForm.name || (!editingStaffLogin && !staffForm.password) || (!staffForm.email && !staffForm.phone)} onClick={saveStaffUser} className="w-full bg-[#111827] text-white hover:bg-[#374151]">
                      {editingStaffLogin ? <Pencil className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />} {editingStaffLogin ? "Update staff login" : "Create staff login"}
                    </Button>
                    {editingStaffLogin ? <Button variant="outline" onClick={cancelStaffEdit}>Cancel</Button> : null}
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredStaffUsers.map((staff) => (
                        <TableRow key={staff.id}>
                          <TableCell className="font-semibold">{staff.name}</TableCell>
                          <TableCell>{staff.email || "-"}</TableCell>
                          <TableCell>{societyStaffRoleLabels[staff.societyRole] || staff.societyRole || "-"}</TableCell>
                          <TableCell>{staff.status || "active"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => editStaffUser(staff)}>Edit</Button>
                              <Button variant="outline" size="sm" onClick={() => resetStaffPassword(staff)}>Password</Button>
                              {staff.status === "suspended" ? <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => activateStaffUser(staff)}>Activate</Button> : <Button variant="outline" size="sm" onClick={() => suspendStaffUser(staff)}>Suspend</Button>}
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteStaffUser(staff)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {staffUsers.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No guard or staff logins created yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="notices" className="mt-5 space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
            <Input value={noticeSearch} onChange={(e) => setNoticeSearch(e.target.value)} className="pl-9" placeholder="Search notice title, message, category or priority" />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-xl font-extrabold text-[#111827]">Society notices and emergency warnings</h3>
              <p className="text-sm text-[#6B7280]">These notices are used by signage widgets and Buddy AI answers.</p>
            </div>
            <Button onClick={() => { setEditingNotice(null); setNoticeForm(noticeBlank); setNoticeValidity("infinite"); setNoticeCustomDate(""); setNoticeOpen(true); }} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Add Notice</Button>
          </div>
          <div className="grid gap-3">
            {filteredNotices.map((notice) => (
              <div key={notice.id} className={`rounded-sm border p-4 ${notice.category === "emergency" || notice.severity === "critical" ? "border-red-200 bg-red-50" : "border-[#E5E7EB] bg-white"}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-extrabold text-[#111827]">{notice.title}</h4>
                      <span className="rounded-sm border border-[#E5E7EB] bg-white px-2 py-0.5 text-xs uppercase tracking-[0.12em] text-[#6B7280]">{notice.category || "notice"}</span>
                      <span className="rounded-sm bg-[#111827] px-2 py-0.5 text-xs uppercase tracking-[0.12em] text-white">{notice.severity || "normal"}</span>
                      {notice.active === false && <span className="rounded-sm bg-[#E5E7EB] px-2 py-0.5 text-xs uppercase tracking-[0.12em] text-[#6B7280]">Inactive</span>}
                      {notice.expires_at && new Date(notice.expires_at).getTime() < Date.now() && <span className="rounded-sm bg-red-100 px-2 py-0.5 text-xs uppercase tracking-[0.12em] text-red-700">Expired</span>}
                    </div>
                    <p className="mt-1 text-xs text-[#9CA3AF]">
                      {notice.created_at ? `Posted ${new Date(notice.created_at).toLocaleString()}` : ""}
                      {notice.expires_at ? ` · Expires ${new Date(notice.expires_at).toLocaleString()}` : " · Never expires"}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-[#4B5563]">{notice.body || "No message body"}</p>
                    {notice.image_url && <div className="mt-2 text-xs text-[#2563EB]">{notice.image_url}</div>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingNotice(notice); setNoticeForm({ ...noticeBlank, ...notice }); setNoticeValidity(notice.expires_at ? "custom" : "infinite"); setNoticeCustomDate(notice.expires_at ? String(notice.expires_at).slice(0, 10) : ""); setNoticeOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => deleteNotice(notice)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {notices.length === 0 && <div className="rounded-sm border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-8 text-center text-sm text-[#6B7280]">No notices added yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="visitor" className="mt-5">
          <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
            <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
              <div className="mb-4 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[#111827]" />
                <h3 className="text-xl font-extrabold">Watchman visitor entry</h3>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Visitor name"><Input value={visitorForm.visitorName} onChange={(e) => setVisitorForm((f) => ({ ...f, visitorName: e.target.value }))} /></Field>
                <Field label="Visitor mobile"><Input value={visitorForm.visitorMobile} onChange={(e) => setVisitorForm((f) => ({ ...f, visitorMobile: e.target.value }))} /></Field>
                <Field label="Visitor photo URL"><Input value={visitorForm.visitorPhoto} onChange={(e) => setVisitorForm((f) => ({ ...f, visitorPhoto: e.target.value }))} placeholder="Upload URL or camera image URL" /></Field>
                <Field label="Purpose"><Input value={visitorForm.purpose} onChange={(e) => setVisitorForm((f) => ({ ...f, purpose: e.target.value }))} /></Field>
                <Field label="Wing"><Input value={visitorForm.wing} onChange={(e) => setVisitorForm((f) => ({ ...f, wing: e.target.value.toUpperCase() }))} /></Field>
                <Field label="Flat number"><Input value={visitorForm.flatNumber} onChange={(e) => setVisitorForm((f) => ({ ...f, flatNumber: e.target.value.toUpperCase() }))} /></Field>
                <Field label="Vehicle number"><Input value={visitorForm.vehicleNumber} onChange={(e) => setVisitorForm((f) => ({ ...f, vehicleNumber: e.target.value }))} /></Field>
                <Field label="Number of visitors"><Input type="number" min="1" value={visitorForm.visitorCount} onChange={(e) => setVisitorForm((f) => ({ ...f, visitorCount: Number(e.target.value) || 1 }))} /></Field>
                <Field label="Gate name"><Input value={visitorForm.gateName} onChange={(e) => setVisitorForm((f) => ({ ...f, gateName: e.target.value }))} /></Field>
                <Field label="Security name"><Input value={visitorForm.securityName} onChange={(e) => setVisitorForm((f) => ({ ...f, securityName: e.target.value }))} /></Field>
                <div className="sm:col-span-2"><Field label="Remarks"><Textarea value={visitorForm.remarks} onChange={(e) => setVisitorForm((f) => ({ ...f, remarks: e.target.value }))} /></Field></div>
              </div>
              <Button disabled={busy} onClick={createVisitor} className="mt-5 min-h-11 bg-[#111827] text-white hover:bg-[#374151]">Create approval request</Button>
            </div>
            <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">Auto fetched owner</div>
              <div className="mt-3 text-2xl font-extrabold text-[#111827]">{selectedVisitorOwner?.ownerName || "No owner matched"}</div>
              <div className="mt-2 text-sm text-[#6B7280]">{selectedVisitorOwner ? `${selectedVisitorOwner.wing} ${selectedVisitorOwner.flatNumber} - ${selectedVisitorOwner.ownerMobile || "mobile hidden"}` : "Enter wing and flat number to fetch resident details."}</div>
              {selectedVisitorOwner?.ownerPhoto && <img src={resolveMediaUrl(selectedVisitorOwner.ownerPhoto)} alt="" className="mt-5 aspect-video w-full rounded-sm object-cover" />}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-5 space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
            <Input value={visitorSearch} onChange={(e) => setVisitorSearch(e.target.value)} className="pl-9" placeholder="Search visitor, flat, resident, mobile or purpose" />
          </div>
          {filteredPendingVisitors.map((visitor) => (
            <div key={visitor.id} className="grid gap-4 rounded-sm border border-[#E5E7EB] bg-white p-4 lg:grid-cols-[96px_1fr_auto] lg:items-center">
              {visitor.visitorPhoto ? <img src={resolveMediaUrl(visitor.visitorPhoto)} alt="" className="h-24 w-24 rounded-sm object-cover" /> : <div className="h-24 w-24 rounded-sm bg-[#EEF2F7]" />}
              <div>
                <div className="text-lg font-extrabold">{visitor.visitorName}</div>
                <div className="text-sm text-[#6B7280]">{visitor.purpose || "Visitor"} for {visitor.wing} {visitor.flatNumber} - {visitor.ownerName || "Owner not found"}</div>
                <div className="mt-2 text-xs text-[#6B7280]">Gate: {visitor.gateName || "-"} | Security: {visitor.securityName || "-"} | Mobile: {visitor.visitorMobile || "-"}</div>
                <div className="mt-2 break-all text-xs text-[#2563EB]">{approvalLink(visitor)}</div>
              </div>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <Button variant="outline" onClick={() => navigator.clipboard?.writeText(approvalLink(visitor))}><MessageCircle className="mr-2 h-4 w-4" /> Copy Link</Button>
                <Button variant="outline" onClick={() => startCall(visitor)}><PhoneCall className="mr-2 h-4 w-4" /> Start Call</Button>
                <Button onClick={() => updateVisitor(visitor, "approve")} className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-2 h-4 w-4" /> Approve</Button>
                <Button variant="outline" onClick={() => updateVisitor(visitor, "reject")}><X className="mr-2 h-4 w-4 text-red-600" /> Reject</Button>
              </div>
            </div>
          ))}
          {pendingVisitors.length === 0 && <div className="rounded-sm border border-dashed border-[#D1D5DB] p-8 text-center text-[#6B7280]">No pending visitor approvals.</div>}
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <div className="relative mb-4">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
            <Input value={visitorSearch} onChange={(e) => setVisitorSearch(e.target.value)} className="pl-9" placeholder="Search visitor history by visitor, flat, resident, status or purpose" />
          </div>
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader><TableRow><TableHead>Visitor</TableHead><TableHead>Flat</TableHead><TableHead>Purpose</TableHead><TableHead>Status</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredHistoryVisitors.map((visitor) => (
                  <TableRow key={visitor.id}>
                    <TableCell className="font-semibold">{visitor.visitorName}</TableCell>
                    <TableCell>{visitor.wing} {visitor.flatNumber}</TableCell>
                    <TableCell>{visitor.purpose || "-"}</TableCell>
                    <TableCell>{visitor.status}</TableCell>
                    <TableCell>{visitor.inTime ? new Date(visitor.inTime).toLocaleString() : "-"}</TableCell>
                    <TableCell>{visitor.outTime ? new Date(visitor.outTime).toLocaleString() : "-"}</TableCell>
                    <TableCell>{visitor.status !== "completed" && <Button variant="outline" size="sm" onClick={() => updateVisitor(visitor, "checkout")}>Checkout</Button>}</TableCell>
                  </TableRow>
                ))}
                {historyVisitors.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-[#6B7280]">No visitor history yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="billing" className="mt-5 space-y-5">
          <div className={`rounded-sm border p-4 ${billingRule?.implemented ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-extrabold text-[#111827]">
                  <ReceiptText className="h-4 w-4" />
                  {billingRule?.implemented ? `${billingRule.state} billing rule active` : "State billing rule not ready"}
                </div>
                <div className="mt-1 text-sm text-[#4B5563]">
                  {billingRule?.implemented
                    ? `${billingRule.billTitle}. Interest limit: ${billingRule.maxInterestRateAnnual}% p.a.`
                    : billingRule?.message || "Set customer registration state before generating bills."}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-sm bg-white p-2"><div className="font-mono text-base font-bold">{bills.length}</div><div className="text-[#6B7280]">Bills</div></div>
                <div className="rounded-sm bg-white p-2"><div className="font-mono text-base font-bold">{pendingPayments.length}</div><div className="text-[#6B7280]">UTR pending</div></div>
                <div className="rounded-sm bg-white p-2"><div className="font-mono text-base font-bold">₹ {outstandingTotal.toLocaleString()}</div><div className="text-[#6B7280]">Outstanding</div></div>
              </div>
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-4 xl:grid-cols-8">
            {[
              ["charges", "Standard Charges", `${chargeHeads.length} heads`],
              ["onetime", "One-time Charges", "Bulk units"],
              ["opening", "Opening Balances", `DR ₹ ${openingDueTotal.toLocaleString()} · CR ₹ ${openingAdvanceTotal.toLocaleString()}`],
              ["settings", "Auto Bill Settings", billingSettings.autoBillingEnabled ? "Auto on" : "Manual"],
              ["generate", "Generate Bills", billingMonth],
              ["outstanding", "Outstanding", `${ownersOutstanding.length} owners`],
              ["payments", "UTR Payments", `${pendingPayments.length} pending`],
              ["whatsapp", "WhatsApp Logs", `${failedWhatsappCount} failed`],
              ["accounting", "Accounting", "Reports"],
            ].map(([key, label, detail]) => key === "accounting" ? (
              <Link key={key} to="/client/society/accounting" className={`rounded-sm border p-3 text-left ${billingMenu === key ? "border-[#111827] bg-[#111827] text-white" : "border-[#E5E7EB] bg-white text-[#111827]"}`}>
                <div className="text-sm font-extrabold">{label}</div>
                <div className="mt-1 text-xs opacity-70">{detail}</div>
              </Link>
            ) : (
              <button key={key} type="button" onClick={() => selectBillingPage(key)} className={`rounded-sm border p-3 text-left ${billingMenu === key ? "border-[#111827] bg-[#111827] text-white" : "border-[#E5E7EB] bg-white text-[#111827]"}`}>
                <div className="text-sm font-extrabold">{label}</div>
                <div className="mt-1 text-xs opacity-70">{detail}</div>
              </button>
            ))}
          </div>

          <div className="space-y-5">
            <div className={billingMenu === "onetime" ? "rounded-sm border border-[#E5E7EB] bg-white p-5" : "hidden"}>
              <h3 className="text-lg font-extrabold text-[#111827]">One-time monthly charge or credit</h3>
              <p className="mt-1 text-sm text-[#6B7280]">Use this for AGM non-attendance penalties or any charge that must apply once to selected units in one billing month.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <Field label="Billing month"><Input type="month" value={oneTimeChargeForm.month} onChange={(e) => setOneTimeChargeForm((form) => ({ ...form, month: e.target.value }))} /></Field>
                <Field label="Charge / credit"><Select value={oneTimeChargeForm.adjustmentType} onValueChange={(adjustmentType) => setOneTimeChargeForm((form) => ({ ...form, adjustmentType }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="debit">Add charge</SelectItem><SelectItem value="credit">Give credit</SelectItem></SelectContent></Select></Field>
                <Field label="Amount"><Input type="number" min="0.01" step="0.01" value={oneTimeChargeForm.amount} onChange={(e) => setOneTimeChargeForm((form) => ({ ...form, amount: e.target.value }))} placeholder="₹ 0" /></Field>
                <Field label="Apply to"><Select value={oneTimeChargeForm.target} onValueChange={(target) => setOneTimeChargeForm((form) => ({ ...form, target }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="residential">All residential flats</SelectItem><SelectItem value="shop">All shops</SelectItem><SelectItem value="office">All offices</SelectItem><SelectItem value="other">All other units</SelectItem><SelectItem value="all">All active units</SelectItem><SelectItem value="selected">Selected units only</SelectItem></SelectContent></Select></Field>
                <Field label="Description"><Input value={oneTimeChargeForm.reason} onChange={(e) => setOneTimeChargeForm((form) => ({ ...form, reason: e.target.value }))} placeholder="AGM non-attendance penalty" /></Field>
              </div>
              {oneTimeChargeForm.target === "selected" && (
                <div className="mt-4 max-h-72 overflow-auto rounded-sm border border-[#E5E7EB]">
                  <Table><TableHeader><TableRow><TableHead className="w-10"><input type="checkbox" aria-label="Select all units" checked={owners.length > 0 && owners.every((owner) => oneTimeOwnerIds.includes(owner.id))} onChange={(e) => setOneTimeOwnerIds(e.target.checked ? owners.map((owner) => owner.id) : [])} /></TableHead><TableHead>Flat / unit</TableHead><TableHead>Owner</TableHead><TableHead>Unit type</TableHead></TableRow></TableHeader><TableBody>{owners.map((owner) => <TableRow key={owner.id}><TableCell><input type="checkbox" checked={oneTimeOwnerIds.includes(owner.id)} onChange={(e) => setOneTimeOwnerIds((ids) => e.target.checked ? Array.from(new Set([...ids, owner.id])) : ids.filter((id) => id !== owner.id))} /></TableCell><TableCell>{owner.wing} {owner.flatNumber}</TableCell><TableCell>{owner.ownerName}</TableCell><TableCell>{owner.unitType || "residential"}</TableCell></TableRow>)}</TableBody></Table>
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-sm bg-[#F8FAFC] p-3">
                <div className="text-sm"><span className="font-bold">{oneTimeTargetOwners.length}</span> active unit{oneTimeTargetOwners.length === 1 ? "" : "s"} will receive this once in {oneTimeChargeForm.month || "the selected month"}.</div>
                <Button disabled={busy || !oneTimeTargetOwners.length} onClick={saveOneTimeCharge} className="bg-[#111827] text-white hover:bg-[#374151]">Save one-time charge</Button>
              </div>
              <div className="mt-5">
                <h4 className="text-sm font-extrabold text-[#111827]">One-time charge and credit register</h4>
                <p className="mt-1 text-xs text-[#6B7280]">Pending entries can be cancelled before bill generation. Applied entries remain linked to their generated bill for audit history.</p>
                <div className="mt-3 max-h-72 overflow-auto rounded-sm border border-[#E5E7EB]">
                  <Table><TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Unit</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>{billAdjustments.length ? billAdjustments.map((item) => <TableRow key={item.id}><TableCell>{item.applyMonth || ""}</TableCell><TableCell>{`${item.wing || ""} ${item.flatNumber || ""}`.trim() || ""}</TableCell><TableCell>{item.adjustmentType === "credit" ? "Credit" : "Charge"}</TableCell><TableCell className="text-right">� {Number(item.amount || 0).toLocaleString()}</TableCell><TableCell>{item.reason || ""}</TableCell><TableCell className="capitalize">{item.status || "pending"}</TableCell><TableCell className="text-right">{item.status === "pending" ? <button type="button" disabled={busy} onClick={() => cancelPendingAdjustment(item)} className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50">Cancel</button> : <span className="text-xs text-[#6B7280]">Linked to bill</span>}</TableCell></TableRow>) : <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-[#6B7280]">No one-time charges or credits.</TableCell></TableRow>}</TableBody>
                  </Table>
                </div>
              </div>
            </div>

            <div className={billingMenu === "opening" ? "rounded-sm border border-[#E5E7EB] bg-white p-5" : "hidden"}>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-lg font-extrabold text-[#111827]">Opening balances before auto billing</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">For DR balances, enter principal and accumulated interest separately. Future interest is calculated only on principal. Use CR only for a member advance.</p>
                </div>
                <div className="flex flex-col gap-2 md:items-end">
                  <div className="rounded-sm bg-[#F8FAFC] px-3 py-2 text-sm text-right">
                    <div>Principal due: <span className="font-mono font-bold">₹ {openingPrincipalTotal.toLocaleString()}</span></div>
                    <div>Interest due: <span className="font-mono font-bold">₹ {openingInterestTotal.toLocaleString()}</span></div>
                    <div>Total DR dues: <span className="font-mono font-bold">₹ {openingDueTotal.toLocaleString()}</span></div>
                    <div>Total CR advances: <span className="font-mono font-bold text-emerald-700">₹ {openingAdvanceTotal.toLocaleString()}</span></div>
                    <div className="mt-1 border-t border-[#E5E7EB] pt-1">Net receivable: <span className="font-mono font-bold">₹ {(openingDueTotal - openingAdvanceTotal).toLocaleString()}</span></div>
                  </div>
                  <Button disabled={busy} onClick={saveOpeningDues} className="bg-[#111827] text-white hover:bg-[#374151]"><Check className="mr-2 h-4 w-4" /> Save opening balances</Button>
                </div>
              </div>
              <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Flat</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className="min-w-[120px]">Type</TableHead>
                      <TableHead className="min-w-[150px] text-right">Principal outstanding</TableHead>
                      <TableHead className="min-w-[150px] text-right">Interest outstanding</TableHead>
                      <TableHead className="min-w-[150px] text-right">Credit / advance</TableHead>
                      <TableHead className="min-w-[260px]">Note</TableHead>
                      <TableHead className="min-w-[160px]">As on date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {owners.filter((owner) => owner.isActive !== false).map((owner) => {
                      const draft = openingDueDrafts[owner.id] || {};
                      return (
                        <TableRow key={owner.id}>
                          <TableCell className="font-semibold">{owner.wing} {owner.flatNumber}</TableCell>
                          <TableCell>{owner.ownerName}</TableCell>
                          <TableCell>
                            <Select value={draft.side || "debit"} onValueChange={(side) => updateOpeningDueDraft(owner.id, side === "credit"
                              ? { side, principalAmount: "", interestAmount: "" }
                              : { side, amount: "" }
                            )}>
                              <SelectTrigger className={draft.side === "credit" ? "font-bold text-emerald-700" : "font-bold text-amber-700"}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="debit">DR · Amount due</SelectItem>
                                <SelectItem value="credit">CR · Advance</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input disabled={draft.side === "credit"} className="text-right font-mono" type="number" min="0" value={draft.principalAmount ?? ""} onChange={(e) => updateOpeningDueDraft(owner.id, { principalAmount: e.target.value })} placeholder="0" />
                          </TableCell>
                          <TableCell>
                            <Input disabled={draft.side === "credit"} className="text-right font-mono" type="number" min="0" value={draft.interestAmount ?? ""} onChange={(e) => updateOpeningDueDraft(owner.id, { interestAmount: e.target.value })} placeholder="0" />
                          </TableCell>
                          <TableCell>
                            <Input disabled={draft.side !== "credit"} className="text-right font-mono" type="number" min="0" value={draft.amount ?? ""} onChange={(e) => updateOpeningDueDraft(owner.id, { amount: e.target.value })} placeholder="0" />
                          </TableCell>
                          <TableCell>
                            <Input value={draft.note || ""} onChange={(e) => updateOpeningDueDraft(owner.id, { note: e.target.value })} placeholder="Balance from old billing" />
                          </TableCell>
                          <TableCell>
                            <Input type="date" value={draft.asOfDate || ""} onChange={(e) => updateOpeningDueDraft(owner.id, { asOfDate: e.target.value })} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {owners.filter((owner) => owner.isActive !== false).length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-[#6B7280]">No active flats found.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Enter balances before generating the first month. Principal and accumulated interest remain separate permanently; new interest is never calculated on the interest amount. CR advances are applied principal-first and any unused credit carries forward.
              </div>
            </div>

            <div className={billingMenu === "settings" ? "space-y-5" : "hidden"}>
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <h3 className="text-lg font-extrabold text-[#111827]">Monthly bill setup</h3>
                <p className="mb-4 mt-1 text-sm text-[#6B7280]">Fill this once. Every month the system creates one bill per active flat using these charges, previous balance, and interest.</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Generate each month on day"><Input type="number" min="1" max="28" value={billingSettings.billingDay} onChange={(e) => setBillingSettings((s) => ({ ...s, billingDay: Number(e.target.value) || 1 }))} /></Field>
                  <Field label="Payment due day next month"><Input type="number" min="1" max="28" value={billingSettings.paymentDueDay || 15} onChange={(e) => setBillingSettings((s) => ({ ...s, paymentDueDay: Number(e.target.value) || 15 }))} /></Field>
                  <Field label="Late payment interest % p.a."><Input type="number" min="0" max="100" step="0.01" value={billingSettings.interestRateAnnual} onChange={(e) => setBillingSettings((s) => ({ ...s, interestRateAnnual: Number(e.target.value) || 0 }))} /></Field>
                  <Field label="Interest rounding">
                    <Select value={billingSettings.interestRoundingMethod || "nearest"} onValueChange={(value) => setBillingSettings((s) => ({ ...s, interestRoundingMethod: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nearest">Nearest rupee</SelectItem>
                        <SelectItem value="upward">Always upward</SelectItem>
                        <SelectItem value="downward">Always downward</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Bill number prefix"><Input value={billingSettings.billPrefix} onChange={(e) => setBillingSettings((s) => ({ ...s, billPrefix: e.target.value.toUpperCase() }))} /></Field>
                  <Field label="Next bill number"><Input type="number" min="1" value={billingSettings.nextBillNumber} onChange={(e) => setBillingSettings((s) => ({ ...s, nextBillNumber: Number(e.target.value) || 1 }))} /></Field>
                  <Field label="UPI ID"><Input value={billingSettings.upiId || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, upiId: e.target.value }))} /></Field>
                  <Field label="Auto billing"><div className="flex h-10 items-center gap-3"><Switch checked={!!billingSettings.autoBillingEnabled} onCheckedChange={(v) => setBillingSettings((s) => ({ ...s, autoBillingEnabled: v }))} /> Generate monthly</div></Field>
                  <Field label="Auto WhatsApp">
                    <div className="flex min-h-10 items-center gap-3">
                      <Switch
                        checked={!!billingSettings.autoWhatsappEnabled}
                        disabled={!billingSettings.autoWhatsappPremiumEnabled}
                        onCheckedChange={(v) => setBillingSettings((s) => ({ ...s, autoWhatsappEnabled: v, whatsappEnabled: v }))}
                      />
                      <span>{billingSettings.autoWhatsappPremiumEnabled ? "Send after generate" : "Premium service not activated. Contact your dealer."}</span>
                    </div>
                  </Field>
                  <Field label="WhatsApp bill template name">
                    <Input value={billingSettings.whatsappTemplateName || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, whatsappTemplateName: e.target.value }))} placeholder="maintenance_bill_notice" />
                    <p className="mt-1 text-xs text-[#9CA3AF]">Name of the Meta-approved Utility template (Admin &gt; Marketing &gt; WhatsApp Templates). Leave blank to use the default "maintenance_bill_notice".</p>
                  </Field>
                  <Field label="WhatsApp template language code">
                    <Input value={billingSettings.whatsappTemplateLanguage || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, whatsappTemplateLanguage: e.target.value }))} placeholder="en_IN" />
                    <p className="mt-1 text-xs text-[#9CA3AF]">Must exactly match the language column in WhatsApp Templates - "English (IND)" = en_IN, "English (US)" = en_US, "English" = en.</p>
                  </Field>
                  <Field label="Bank name"><Input value={billingSettings.bankName || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, bankName: e.target.value }))} /></Field>
                  <Field label="IFSC"><Input value={billingSettings.ifsc || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, ifsc: e.target.value.toUpperCase() }))} /></Field>
                  <div className="sm:col-span-2"><Field label="Bank account name"><Input value={billingSettings.bankAccountName || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, bankAccountName: e.target.value }))} /></Field></div>
                  <div className="sm:col-span-2"><Field label="Bank account number"><Input value={billingSettings.bankAccountNumber || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, bankAccountNumber: e.target.value }))} /></Field></div>
                  <div className="sm:col-span-2"><Field label="Bill footer note"><Textarea rows={3} value={billingSettings.notes || ""} onChange={(e) => setBillingSettings((s) => ({ ...s, notes: e.target.value }))} placeholder="Example: Please pay before due date. Late payment interest will apply on outstanding balance." /></Field></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button disabled={busy} onClick={saveBillingSettings} className="bg-[#111827] text-white hover:bg-[#374151]">Save monthly setup</Button>
                  <Button disabled={busy || !billingSettings.autoBillingEnabled} variant="outline" onClick={runBillingScheduler}>Run due billing now</Button>
                </div>
                {billingRuns.length > 0 && (
                  <div className="mt-5 rounded-sm border border-[#E5E7EB]">
                    {billingRuns.slice(0, 4).map((run) => (
                      <div key={run.id || run.runKey} className="border-b border-[#F1F5F9] px-3 py-2 text-xs last:border-b-0">
                        <div className="font-semibold text-[#111827]">{run.billingMonth} | {run.status}</div>
                        <div className="text-[#6B7280]">Generated {run.result?.generated ?? "-"} | WhatsApp sent {run.whatsapp?.sent ?? 0}, failed {run.whatsapp?.failed ?? 0}</div>
                        {run.error && <div className="mt-1 text-red-600">{run.error}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            <div className={billingMenu === "charges" ? "space-y-5" : "hidden"}>
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-extrabold text-[#111827]">Standard monthly charges</h3>
                    <p className="mt-1 text-sm text-[#6B7280]">Maintain independent monthly charge lists for flats, shops and offices. Billing automatically selects the correct list from each unit's Unit Type.</p>
                  </div>
                  {chargeGroup === "flat" && <Button type="button" variant="outline" size="sm" onClick={seedChargeHeads}>Add state heads</Button>}
                </div>
                <div className="mb-4 max-w-md">
                  <Field label="Standard charge list">
                    <Select value={chargeGroup} onValueChange={(unitGroup) => { setChargeGroup(unitGroup); setEditingChargeId(null); setChargeForm({ ...chargeHeadBlank, unitGroup }); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="flat">Standard Charges for Flats</SelectItem><SelectItem value="shop">Standard Charges for Shops</SelectItem><SelectItem value="office">Standard Charges for Offices</SelectItem></SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {standardChargeTemplates.map((template) => (
                    <Button key={template.code} type="button" variant="outline" size="sm" onClick={() => applyChargeTemplate(template)}>{template.label}</Button>
                  ))}
                </div>
                <div className="grid gap-3">
                  <Field label="Charge name"><Input value={chargeForm.name} onChange={(e) => setChargeForm((f) => ({ ...f, name: e.target.value }))} placeholder="Water charges" /></Field>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Code"><Input value={chargeForm.code} onChange={(e) => setChargeForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} /></Field>
                    <Field label={allocationRateLabel(chargeForm.allocation)}><Input type="number" min="0" step={chargeForm.allocation === "area" ? "0.0001" : "0.01"} value={chargeForm.defaultAmount} onChange={(e) => setChargeForm((f) => ({ ...f, defaultAmount: e.target.value }))} /></Field>
                    <Field label="Calculation">
                      <Select value={chargeForm.allocation} onValueChange={(value) => setChargeForm((f) => ({ ...f, allocation: value }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Fixed amount per flat</SelectItem>
                          <SelectItem value="area">Rate x flat area sq ft</SelectItem>
                          <SelectItem value="vehicle_2w">Rate x number of 2-wheelers</SelectItem>
                          <SelectItem value="vehicle_4w">Rate x number of 4-wheelers</SelectItem>
                          <SelectItem value="vehicle_cycle">Rate x number of cycles</SelectItem>
                          <SelectItem value="manual">Manual / special charge</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Category">
                      <Select value={chargeForm.category} onValueChange={(value) => setChargeForm((f) => ({ ...f, category: value }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="regular">Regular</SelectItem>
                          <SelectItem value="utility">Utility</SelectItem>
                          <SelectItem value="fund">Fund</SelectItem>
                          <SelectItem value="tax">Tax</SelectItem>
                          <SelectItem value="statutory">Statutory</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Applies to">
                      <Select value={chargeForm.applicability || "all"} onValueChange={(value) => setChargeForm((f) => ({ ...f, applicability: value }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All {chargeGroup === "flat" ? "flats" : chargeGroup === "shop" ? "shops" : "offices"}</SelectItem>
                          {chargeGroup === "flat" && <SelectItem value="residential">All residential flats</SelectItem>}
                          {chargeGroup !== "flat" && <SelectItem value="commercial">All units in this commercial list</SelectItem>}
                          <SelectItem value="tenant_occupied">Tenant-occupied units only</SelectItem>
                          <SelectItem value="tenant_deed_pending">Tenant occupied — deed pending</SelectItem>
                          <SelectItem value="owner_occupied">Owner-occupied units only</SelectItem>
                          <SelectItem value="vacant">Vacant units only</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Button disabled={busy || !chargeForm.name} onClick={addChargeHead} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Add charge</Button>
                </div>
                <div className="mt-5 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[90px]">Sequence</TableHead>
                        <TableHead>Charge head</TableHead>
                        <TableHead>Amount / rate</TableHead>
                        <TableHead>Calculation</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Applies to</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleChargeHeads.map((head, index) => (
                        <TableRow key={head.id}>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={busy || index === 0} onClick={() => moveChargeHead(head.id, -1)} title="Move up"><ArrowUp className="h-4 w-4" /></Button>
                              <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={busy || index === visibleChargeHeads.length - 1} onClick={() => moveChargeHead(head.id, 1)} title="Move down"><ArrowDown className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input disabled={editingChargeId !== head.id} value={head.name || ""} onChange={(e) => updateChargeHeadLocal(head.id, { name: e.target.value })} />
                            <div className="mt-1 text-xs text-[#6B7280]">{head.code || "-"}</div>
                          </TableCell>
                          <TableCell>
                            <Input disabled={editingChargeId !== head.id} className="w-36" type="number" min="0" step={head.allocation === "area" ? "0.0001" : "0.01"} value={head.defaultAmount ?? 0} onChange={(e) => updateChargeHeadLocal(head.id, { defaultAmount: e.target.value })} />
                            <div className="mt-1 text-xs text-[#6B7280]">{allocationRateLabel(head.allocation)}</div>
                          </TableCell>
                          <TableCell>
                            <Select disabled={editingChargeId !== head.id} value={head.allocation || "flat"} onValueChange={(value) => updateChargeHeadLocal(head.id, { allocation: value })}>
                              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="flat">Fixed per flat</SelectItem>
                                <SelectItem value="area">Rate x area sq ft</SelectItem>
                                <SelectItem value="vehicle_2w">Rate x 2-wheelers</SelectItem>
                                <SelectItem value="vehicle_4w">Rate x 4-wheelers</SelectItem>
                                <SelectItem value="vehicle_cycle">Rate x cycles</SelectItem>
                                <SelectItem value="manual">Manual/special</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select disabled={editingChargeId !== head.id} value={head.category || "regular"} onValueChange={(value) => updateChargeHeadLocal(head.id, { category: value })}>
                              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="regular">Regular</SelectItem>
                                <SelectItem value="utility">Utility</SelectItem>
                                <SelectItem value="fund">Fund</SelectItem>
                                <SelectItem value="tax">Tax</SelectItem>
                                <SelectItem value="statutory">Statutory</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select disabled={editingChargeId !== head.id} value={head.applicability || "all"} onValueChange={(value) => updateChargeHeadLocal(head.id, { applicability: value })}>
                              <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All {chargeGroup === "flat" ? "flats" : chargeGroup === "shop" ? "shops" : "offices"}</SelectItem>
                                {chargeGroup === "flat" && <SelectItem value="residential">All residential flats</SelectItem>}
                                {chargeGroup !== "flat" && <SelectItem value="commercial">All units in this commercial list</SelectItem>}
                                <SelectItem value="tenant_occupied">Tenant-occupied only</SelectItem>
                                <SelectItem value="tenant_deed_pending">Tenant occupied — deed pending</SelectItem>
                                <SelectItem value="owner_occupied">Owner-occupied only</SelectItem>
                                <SelectItem value="vacant">Vacant units only</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>{head.active === false ? "Disabled" : "Active"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => toggleChargeHead(head)}>{head.active === false ? "Enable" : "Disable"}</Button>
                              {editingChargeId === head.id ? (
                                <>
                                  <Button variant="outline" size="sm" disabled={busy} onClick={async () => { setEditingChargeId(null); await load(); }}>Cancel</Button>
                                  <Button size="sm" disabled={busy} onClick={() => saveChargeHead(head)} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="outline" size="sm" disabled={busy} onClick={() => setEditingChargeId(head.id)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                                  <Button variant="outline" size="sm" disabled={busy} className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteChargeHead(head)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {visibleChargeHeads.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-[#6B7280]">No charges in this list yet. Add the first charge above.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            <div className={billingMenu === "generate" ? "space-y-5" : "hidden"}>
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <div className="mb-4 rounded-sm border border-[#DBEAFE] bg-[#EFF6FF] p-3 text-sm text-[#1E3A8A]">
                  Monthly bill = active standard charges + previous unpaid balance + late payment interest + manual adjustments − receipts and available member credit.
                  Charges marked per sq ft use each flat's Area sq ft from Owner Directory.
                </div>
                {flatsMissingArea > 0 && (
                  <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    {flatsMissingArea} active flat{flatsMissingArea === 1 ? "" : "s"} missing Area sq ft. Per-sq-ft charges will be zero for those flats until area is filled in Owner Directory.
                  </div>
                )}
                <div className="sticky top-2 z-30 flex flex-col gap-3 rounded-sm border border-[#E5E7EB] bg-white p-3 shadow-sm md:flex-row md:items-end md:justify-between">
                  <div className="grid flex-1 gap-3 md:grid-cols-2">
                    <Field label="Generation month">
                      <Input type="month" value={generationMonth} onChange={(e) => { const month = e.target.value; setGenerationMonth(month); setBillingMonth(previousMonthValue(month)); }} />
                      <div className="mt-1 text-xs font-semibold text-[#1E3A8A]">Bill period: {monthLabel(previousMonthValue(generationMonth))}</div>
                    </Field>
                    <Field label="Search bills"><div className="relative"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" /><Input value={billSearch} onChange={(e) => setBillSearch(e.target.value)} className="pl-9" placeholder="Bill, owner, flat, mobile or status" /></div></Field>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedBillIds.some((id) => filteredMonthBills.some((bill) => bill.id === id)) && (
                      <Button disabled={busy} variant="outline" onClick={bulkDeleteBills} className="border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="mr-2 h-4 w-4" /> Delete selected</Button>
                    )}
                    <Button disabled={busy || monthBills.length === 0} variant="outline" onClick={downloadMonthlyBillsPdf}><Download className="mr-2 h-4 w-4" /> Download all bills PDF</Button>
                    <Button disabled={busy || !billingRule?.implemented} onClick={generateBills} className="bg-[#111827] text-white hover:bg-[#374151]"><IndianRupee className="mr-2 h-4 w-4" /> Generate monthly bills</Button>
                  </div>
                </div>
                {selectedBillIds.some((id) => filteredMonthBills.some((bill) => bill.id === id)) && (
                  <div className="mt-3 rounded-sm border border-[#BFDBFE] bg-[#EFF6FF] p-3">
                    <div className="mb-2 text-sm font-extrabold text-[#1E3A8A]">
                      Add one-time charge to {selectedBillIds.filter((id) => monthBills.some((bill) => bill.id === id)).length} selected flat/shop bill(s)
                    </div>
                    <div className="grid gap-2 md:grid-cols-[2fr_1fr_2fr_auto] md:items-end">
                      <Field label="Charge description"><Input value={selectedBillCharge.name} onChange={(e) => setSelectedBillCharge((form) => ({ ...form, name: e.target.value }))} placeholder="FOR NOT ATTENDING AGM" /></Field>
                      <Field label="Amount per bill"><Input type="number" min="0.01" step="0.01" value={selectedBillCharge.amount} onChange={(e) => setSelectedBillCharge((form) => ({ ...form, amount: e.target.value }))} placeholder="₹ 500" /></Field>
                      <Field label="Committee decision / note"><Input value={selectedBillCharge.reason} onChange={(e) => setSelectedBillCharge((form) => ({ ...form, reason: e.target.value }))} placeholder="Resolution number or reason (optional)" /></Field>
                      <Button disabled={busy || !selectedBillCharge.name.trim() || Number(selectedBillCharge.amount) <= 0} onClick={addChargeToSelectedBills} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Add to selected</Button>
                    </div>
                    <div className="mt-2 text-xs text-[#1E3A8A]">The charge is added immediately to only the checked generated bills. Their existing accounting vouchers are updated in place; unselected bills are untouched.</div>
                  </div>
                )}
                <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead className="w-10"><input type="checkbox" aria-label="Select all visible bills" checked={filteredMonthBills.length > 0 && filteredMonthBills.every((bill) => selectedBillIds.includes(bill.id))} onChange={(e) => setSelectedBillIds(e.target.checked ? Array.from(new Set([...selectedBillIds, ...filteredMonthBills.map((bill) => bill.id)])) : selectedBillIds.filter((id) => !filteredMonthBills.some((bill) => bill.id === id)))} /></TableHead><TableHead>Bill</TableHead><TableHead>Flat</TableHead><TableHead>Owner</TableHead><TableHead>Total</TableHead><TableHead>Receipt / credit used</TableHead><TableHead>Credit left</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead><TableHead>Accounting</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredMonthBills.map((bill) => (
                        <TableRow key={bill.id}>
                          <TableCell><input type="checkbox" aria-label={`Select bill ${bill.billNumber || ""}`} checked={selectedBillIds.includes(bill.id)} onChange={(e) => setSelectedBillIds((ids) => e.target.checked ? Array.from(new Set([...ids, bill.id])) : ids.filter((id) => id !== bill.id))} /></TableCell>
                          <TableCell className="font-mono text-xs">{bill.billNumber}</TableCell>
                          <TableCell>{bill.wing} {bill.flatNumber}</TableCell>
                          <TableCell>{bill.ownerName}</TableCell>
                          <TableCell>₹ {Number(bill.totalDue || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-emerald-700">₹ {Number(bill.creditApplied || 0).toLocaleString()}</TableCell>
                          <TableCell className="font-semibold text-emerald-700">₹ {Number(bill.creditBalance || 0).toLocaleString()}</TableCell>
                          <TableCell>₹ {Number(bill.balanceDue || 0).toLocaleString()}</TableCell>
                          <TableCell>{bill.status}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {accountingStatusBadge(bill.billingAccountingStatus)}
                              {bill.billingAccountingStatus === "posted" && bill.billingAccountingVoucherId && (
                                <Link className="text-xs text-[#111827] underline" to={`/client/society/accounting/vouchers/${bill.billingAccountingVoucherId}`}>{bill.billingAccountingVoucherNumber || "View voucher"}</Link>
                              )}
                              {bill.billingAccountingStatus === "error" && (
                                <>
                                  <span className="max-w-[220px] truncate text-xs text-red-600" title={bill.billingAccountingError}>{bill.billingAccountingError}</span>
                                  <Button variant="outline" size="sm" onClick={() => retryBillAccounting(bill)}>Retry posting</Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => openBillEditor(bill)}>Edit bill</Button>
                              <Button variant="outline" size="sm" onClick={() => downloadBillPdf(bill)}><Download className="h-4 w-4" /></Button>
                              <Button variant="outline" size="sm" onClick={() => sendBillWhatsapp(bill)}><Send className="h-4 w-4" /></Button>
                              {bill.status !== "cancelled" && Number(bill.paidAmount || 0) <= 0 && <Button variant="outline" size="sm" onClick={() => cancelBill(bill)}>Cancel</Button>}
                              <Button disabled={busy} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteBill(bill)} title="Delete bill"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {monthBills.length === 0 && <TableRow><TableCell colSpan={11} className="py-8 text-center text-[#6B7280]">No bills for selected month.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
                {advancedAccountingOpen && (
                <div className="mt-5 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4">
                  <div className="mb-3 text-sm font-extrabold text-[#111827]">Financial year close / audit lock</div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <Field label="From date"><Input type="date" value={financialCloseForm.fromDate} onChange={(e) => setFinancialCloseForm((f) => ({ ...f, fromDate: e.target.value }))} /></Field>
                    <Field label="To date"><Input type="date" value={financialCloseForm.toDate} onChange={(e) => setFinancialCloseForm((f) => ({ ...f, toDate: e.target.value }))} /></Field>
                    <Field label="Reason"><Input value={financialCloseForm.reason} onChange={(e) => setFinancialCloseForm((f) => ({ ...f, reason: e.target.value }))} placeholder="CA reviewed and finalized" /></Field>
                    <div className="md:col-span-3"><Button disabled={busy || !financialCloseForm.fromDate || !financialCloseForm.toDate || !financialCloseForm.reason} onClick={closeFinancialPeriod} className="bg-[#111827] text-white hover:bg-[#374151]">Close period</Button></div>
                  </div>
                  <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB] bg-white">
                    <Table>
                      <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {financialCloses.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.fromDate}</TableCell>
                            <TableCell>{row.toDate}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell>{row.reason || row.reopenReason || "-"}</TableCell>
                            <TableCell>{row.closedBy || row.reopenedBy || "-"}</TableCell>
                            <TableCell className="text-right">{row.status === "closed" && user?.role !== "society_staff" ? <Button variant="outline" size="sm" onClick={() => reopenFinancialPeriod(row)}>Reopen</Button> : "-"}</TableCell>
                          </TableRow>
                        ))}
                        {financialCloses.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No financial periods closed yet.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                )}
              </div>

            </div>

            <div className={billingMenu === "outstanding" ? "space-y-5" : "hidden"}>
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-extrabold text-[#111827]">Owners with outstanding balance</h3>
                    <p className="mt-1 text-sm text-[#6B7280]">All unpaid/partial bills across every month, grouped by owner and sorted highest balance first. Total outstanding: ₹ {outstandingTotal.toLocaleString()}.</p>
                  </div>
                </div>
                <div className="relative mb-4">
                  <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
                  <Input value={outstandingSearch} onChange={(e) => setOutstandingSearch(e.target.value)} className="pl-9" placeholder="Search outstanding by owner, flat, wing or mobile" />
                </div>
                <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Flat</TableHead><TableHead>Owner</TableHead><TableHead>Mobile</TableHead><TableHead>Unpaid bills</TableHead><TableHead>Outstanding</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredOwnersOutstanding.map((row) => (
                        <TableRow key={row.key}>
                          <TableCell>{row.wing} {row.flatNumber}</TableCell>
                          <TableCell>{row.ownerName || "-"}</TableCell>
                          <TableCell>{row.ownerMobile || "-"}</TableCell>
                          <TableCell>{row.unpaidBills}</TableCell>
                          <TableCell className="font-semibold">₹ {row.outstanding.toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => downloadBillPdf(row.latestBill)}><Download className="h-4 w-4" /></Button>
                              <Button variant="outline" size="sm" onClick={() => sendBillWhatsapp(row.latestBill)}><Send className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {ownersOutstanding.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No outstanding balances. Everyone is paid up.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

              <div className={billingMenu === "payments" ? "rounded-sm border border-[#E5E7EB] bg-white p-5" : "hidden"}>
                <h3 className="mb-4 text-lg font-extrabold text-[#111827]">UTR verification</h3>
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
                  <div className="relative flex-1"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" /><Input value={paymentSearch} onChange={(e) => setPaymentSearch(e.target.value)} className="pl-9" placeholder="Search UTR, bill, owner, receipt or status" /></div>
                  <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}><SelectTrigger className="w-full md:w-52"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="pending_verification">Pending verification</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select>
                  <Button disabled={busy || !selectedPaymentIds.some((id) => payments.some((row) => row.id === id && row.status === "pending_verification"))} onClick={() => bulkVerifyPayments(true)} className="bg-emerald-600 text-white hover:bg-emerald-700">Accept selected</Button>
                  <Button variant="outline" disabled={busy || !selectedPaymentIds.some((id) => payments.some((row) => row.id === id && row.status === "pending_verification"))} onClick={() => bulkVerifyPayments(false)}>Reject selected</Button>
                </div>
                <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead className="w-10"><input type="checkbox" aria-label="Select all visible pending payments" checked={filteredPayments.filter((row) => row.status === "pending_verification").length > 0 && filteredPayments.filter((row) => row.status === "pending_verification").every((row) => selectedPaymentIds.includes(row.id))} onChange={(e) => { const visible = filteredPayments.filter((row) => row.status === "pending_verification"); setSelectedPaymentIds(e.target.checked ? Array.from(new Set([...selectedPaymentIds, ...visible.map((row) => row.id)])) : selectedPaymentIds.filter((id) => !visible.some((row) => row.id === id))); }} /></TableHead><TableHead>Bill</TableHead><TableHead>Owner</TableHead><TableHead>Amount</TableHead><TableHead>UTR</TableHead><TableHead>Status</TableHead><TableHead>Receipt</TableHead><TableHead>Proof</TableHead><TableHead>Accounting</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filteredPayments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.status === "pending_verification" ? <input type="checkbox" aria-label={`Select payment ${payment.utr || payment.id}`} checked={selectedPaymentIds.includes(payment.id)} onChange={(e) => setSelectedPaymentIds((ids) => e.target.checked ? Array.from(new Set([...ids, payment.id])) : ids.filter((id) => id !== payment.id))} /> : null}</TableCell>
                          <TableCell className="font-mono text-xs">{payment.billNumber || payment.billId}</TableCell>
                          <TableCell>{payment.ownerName || "-"}</TableCell>
                          <TableCell>₹ {Number(payment.amount || 0).toLocaleString()}</TableCell>
                          <TableCell className="font-mono text-xs">{payment.utr || "-"}</TableCell>
                          <TableCell>{payment.status}</TableCell>
                          <TableCell className="font-mono text-xs">{payment.receiptNumber || "-"}</TableCell>
                          <TableCell>
                            <label className="inline-flex cursor-pointer items-center rounded-sm border border-[#D1D5DB] px-2 py-1 text-xs font-semibold hover:bg-[#F9FAFB]">
                              Upload
                              <input className="hidden" type="file" accept="image/*,application/pdf" onChange={(e) => uploadAttachment("payment", payment.id, e.target.files?.[0], "Payment proof")} />
                            </label>
                            {attachmentCount("payment", payment.id) > 0 && <span className="ml-2 text-xs text-[#6B7280]">{attachmentCount("payment", payment.id)}</span>}
                          </TableCell>
                          <TableCell>
                            {payment.status === "accepted" ? (
                              <div className="flex flex-col gap-1">
                                {accountingStatusBadge(payment.paymentAccountingStatus)}
                                {payment.paymentAccountingStatus === "posted" && payment.paymentAccountingVoucherId && (
                                  <Link className="text-xs text-[#111827] underline" to={`/client/society/accounting/vouchers/${payment.paymentAccountingVoucherId}`}>{payment.paymentAccountingVoucherNumber || "View voucher"}</Link>
                                )}
                                {payment.paymentAccountingStatus === "error" && (
                                  <>
                                    <span className="max-w-[220px] truncate text-xs text-red-600" title={payment.paymentAccountingError}>{payment.paymentAccountingError}</span>
                                    <Button variant="outline" size="sm" onClick={() => retryPaymentAccounting(payment)}>Retry posting</Button>
                                  </>
                                )}
                              </div>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {payment.status === "pending_verification" ? (
                              <div className="flex justify-end gap-2">
                                <Button size="sm" onClick={() => verifyPayment(payment, true)} className="bg-emerald-600 text-white hover:bg-emerald-700">Accept</Button>
                                <Button size="sm" variant="outline" onClick={() => verifyPayment(payment, false)}>Reject</Button>
                              </div>
                            ) : payment.status === "accepted" ? (
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => downloadReceiptPdf(payment)}><Download className="h-4 w-4" /></Button>
                                <Button variant="outline" size="sm" onClick={() => sendReceiptWhatsapp(payment)}><Send className="h-4 w-4" /></Button>
                                <Button disabled={busy} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deletePayment(payment)} title="Delete erroneous payment"><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            ) : payment.status === "rejected" ? (
                              <Button disabled={busy} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deletePayment(payment)} title="Delete rejected test payment"><Trash2 className="h-4 w-4" /></Button>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredPayments.length === 0 && <TableRow><TableCell colSpan={10} className="py-8 text-center text-[#6B7280]">No matching payment entries.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>

            <div className={billingMenu === "whatsapp" ? "rounded-sm border border-[#E5E7EB] bg-white p-5" : "hidden"}>
                <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-extrabold text-[#111827]">WhatsApp delivery</h3>
                    <div className="mt-1 text-sm text-[#6B7280]">Latest bill and receipt message attempts</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => downloadAuditReport("whatsapp_logs")}><Download className="mr-2 h-4 w-4" /> Export</Button>
                </div>
                <div className="relative mb-4"><Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" /><Input value={whatsappSearch} onChange={(e) => setWhatsappSearch(e.target.value)} className="pl-9" placeholder="Search reference, phone, type, status or error" /></div>
                <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWhatsappLogs.slice(0, 20).map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap text-xs">{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</TableCell>
                          <TableCell className="capitalize">{log.messageType || "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{log.billNumber || log.receiptNumber || log.entityId || "-"}</TableCell>
                          <TableCell className="font-mono text-xs">{log.phone || "-"}</TableCell>
                          <TableCell>
                            <span className={`inline-flex rounded-sm px-2 py-1 text-xs font-bold ${log.status === "failed" ? "bg-red-50 text-red-700" : ["delivered", "read"].includes(log.status) ? "bg-emerald-50 text-emerald-700" : log.status === "sent" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{log.status === "sent" ? "sent (awaiting delivery)" : log.status || "-"}</span>
                          </TableCell>
                          <TableCell>{log.attempts || 1}</TableCell>
                          <TableCell className="max-w-[280px] truncate text-xs text-[#6B7280]">{log.failureReason || log.error || "-"}</TableCell>
                          <TableCell className="text-right">
                            {log.status === "failed" ? <Button variant="outline" size="sm" onClick={() => retryWhatsappLog(log)}>Retry</Button> : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                      {whatsappLogs.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-[#6B7280]">No WhatsApp delivery logs yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {false && user?.role !== "society_staff" && (
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <h3 className="mb-4 text-lg font-extrabold text-[#111827]">Member app logins</h3>
                <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 md:grid-cols-3">
                  <Field label="Flat owner">
                    <Select value={memberForm.ownerId || "none"} onValueChange={(v) => {
                      const owner = owners.find((item) => item.id === v);
                      setMemberForm((f) => ({
                        ...f,
                        ownerId: v === "none" ? "" : v,
                        name: owner?.ownerName || f.name,
                        email: owner?.ownerEmail || f.email,
                        phone: owner?.ownerMobile || f.phone,
                        societyRole: owner?.committeeRole || f.societyRole || "flat_owner",
                      }));
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select flat</SelectItem>
                        {owners.map((owner) => <SelectItem key={owner.id} value={owner.id}>{ownerLabel(owner)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Member name"><Input value={memberForm.name} onChange={(e) => setMemberForm((f) => ({ ...f, name: e.target.value }))} /></Field>
                  <Field label="Mobile"><Input value={memberForm.phone} onChange={(e) => setMemberForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label="Email (optional)"><Input type="email" value={memberForm.email} onChange={(e) => setMemberForm((f) => ({ ...f, email: e.target.value }))} /></Field>
                  <Field label="Member role">
                    <Select value={memberForm.societyRole || "flat_owner"} onValueChange={(v) => setMemberForm((f) => ({ ...f, societyRole: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat_owner">Flat Owner</SelectItem>
                        <SelectItem value="chairman">Chairman</SelectItem>
                        <SelectItem value="secretary">Secretary</SelectItem>
                        <SelectItem value="treasurer">Treasurer</SelectItem>
                        <SelectItem value="committee_member">Committee member</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Password"><Input type="password" value={memberForm.password} onChange={(e) => setMemberForm((f) => ({ ...f, password: e.target.value }))} /></Field>
                  <div className="flex items-end"><Button disabled={busy || !memberForm.ownerId || !memberForm.password || (!memberForm.email && !memberForm.phone)} onClick={addMemberUser} className="w-full bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Create member login</Button></div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Flat</TableHead><TableHead>Mobile</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {memberUsers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-semibold">{member.name}</TableCell>
                          <TableCell>{member.wing} {member.flatNumber}</TableCell>
                          <TableCell>{member.phone || member.ownerMobile || "-"}</TableCell>
                          <TableCell>{member.email || "-"}</TableCell>
                          <TableCell>{member.status || "active"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => resetMemberPassword(member)}>Password</Button>
                              {member.status === "suspended" ? <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => activateMemberUser(member)}>Activate</Button> : <Button variant="outline" size="sm" onClick={() => suspendMemberUser(member)}>Suspend</Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {memberUsers.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">No member app logins created.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
              )}

              {false && user?.role !== "society_staff" && (
              <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
                <h3 className="mb-4 text-lg font-extrabold text-[#111827]">Society staff logins</h3>
                <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 md:grid-cols-3">
                  <Field label="Staff name"><Input value={staffForm.name} onChange={(e) => setStaffForm((f) => ({ ...f, name: e.target.value }))} /></Field>
                  <Field label="Email (optional)"><Input type="email" value={staffForm.email} onChange={(e) => setStaffForm((f) => ({ ...f, email: e.target.value }))} /></Field>
                  <Field label="Password"><Input type="password" value={staffForm.password} onChange={(e) => setStaffForm((f) => ({ ...f, password: e.target.value }))} /></Field>
                  <Field label="Phone"><Input value={staffForm.phone} onChange={(e) => setStaffForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label="Role">
                    <Select value={staffForm.societyRole} onValueChange={(v) => setStaffForm((f) => ({ ...f, societyRole: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="security">Guard / Security</SelectItem>
                        <SelectItem value="office_staff">Office Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="md:col-span-3"><Button disabled={busy || !staffForm.name || !staffForm.password || (!staffForm.email && !staffForm.phone)} onClick={addStaffUser} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Create staff login</Button></div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {staffUsers.map((staff) => (
                        <TableRow key={staff.id}>
                          <TableCell className="font-semibold">{staff.name}</TableCell>
                          <TableCell>{staff.email || "-"}</TableCell>
                          <TableCell>{staff.societyRole || "-"}</TableCell>
                          <TableCell>{staff.status || "active"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => resetStaffPassword(staff)}>Password</Button>
                              {staff.status === "suspended" ? <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => activateStaffUser(staff)}>Activate</Button> : <Button variant="outline" size="sm" onClick={() => suspendStaffUser(staff)}>Suspend</Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {staffUsers.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-[#6B7280]">No staff logins created.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </div>
              )}

            </div>
        </TabsContent>

        <TabsContent value="display" className="mt-5">
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <div className="mb-5 flex items-center gap-2"><Settings className="h-5 w-5" /><h3 className="text-xl font-extrabold">Android signage directory zone settings</h3></div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Directory zone"><div className="flex h-10 items-center gap-3"><Switch checked={settings.enableDirectoryZone} onCheckedChange={(v) => setSettings((s) => ({ ...s, enableDirectoryZone: v }))} /> Enable</div></Field>
              <Field label="Visitor pending zone"><div className="flex h-10 items-center gap-3"><Switch checked={settings.enableVisitorPendingZone} onCheckedChange={(v) => setSettings((s) => ({ ...s, enableVisitorPendingZone: v }))} /> Enable</div></Field>
              <Field label="Display mode">
                <Select value={settings.directoryDisplayMode} onValueChange={(v) => setSettings((s) => ({ ...s, directoryDisplayMode: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wing_rotation">Wing-wise auto rotation</SelectItem>
                    <SelectItem value="selected_wing">Selected wing only</SelectItem>
                    <SelectItem value="full_scroll">Full directory scrolling</SelectItem>
                    <SelectItem value="compact">Compact</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Selected wing"><Input value={settings.selectedWing} onChange={(e) => setSettings((s) => ({ ...s, selectedWing: e.target.value || "All" }))} /></Field>
              <Field label="Rotation interval seconds"><Input type="number" value={settings.rotationInterval} onChange={(e) => setSettings((s) => ({ ...s, rotationInterval: Number(e.target.value) || 15 }))} /></Field>
              <Field label="Rows per page"><Input type="number" value={settings.rowsPerPage} onChange={(e) => setSettings((s) => ({ ...s, rowsPerPage: Number(e.target.value) || 8 }))} /></Field>
              <Field label="Font size"><Input type="number" value={settings.fontSize} onChange={(e) => setSettings((s) => ({ ...s, fontSize: Number(e.target.value) || 24 }))} /></Field>
              <Field label="Display style">
                <Select value={settings.displayStyle} onValueChange={(v) => setSettings((s) => ({ ...s, displayStyle: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="list">List</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="compact">Compact</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Prompt duration seconds"><Input type="number" value={settings.promptDurationSeconds} onChange={(e) => setSettings((s) => ({ ...s, promptDurationSeconds: Number(e.target.value) || 30 }))} /></Field>
              <Field label="Background color"><Input value={settings.backgroundColor} onChange={(e) => setSettings((s) => ({ ...s, backgroundColor: e.target.value }))} /></Field>
              <Field label="Text color"><Input value={settings.textColor} onChange={(e) => setSettings((s) => ({ ...s, textColor: e.target.value }))} /></Field>
              <Field label="Visible columns">
                <div className="flex flex-wrap gap-4 pt-2 text-sm">
                  <label className="flex items-center gap-2"><Switch checked={settings.showOwnerPhoto} onCheckedChange={(v) => setSettings((s) => ({ ...s, showOwnerPhoto: v }))} /> Photo</label>
                  <label className="flex items-center gap-2"><Switch checked={settings.showMobile} onCheckedChange={(v) => setSettings((s) => ({ ...s, showMobile: v }))} /> Mobile</label>
                  <label className="flex items-center gap-2"><Switch checked={settings.showFlatNumber} onCheckedChange={(v) => setSettings((s) => ({ ...s, showFlatNumber: v }))} /> Flat</label>
                </div>
              </Field>
            </div>
            <Button onClick={saveSettings} className="mt-5 bg-[#111827] text-white hover:bg-[#374151]">Save display settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="voice" className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <div className="mb-4 flex items-center gap-2"><Monitor className="h-5 w-5" /><h3 className="text-xl font-extrabold">Display owner on device</h3></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Owner name"><Input value={displayForm.ownerName} onChange={(e) => setDisplayForm((f) => ({ ...f, ownerName: e.target.value }))} /></Field>
              <Field label="Wing"><Input value={displayForm.wing} onChange={(e) => setDisplayForm((f) => ({ ...f, wing: e.target.value.toUpperCase() }))} /></Field>
              <Field label="Flat number"><Input value={displayForm.flatNumber} onChange={(e) => setDisplayForm((f) => ({ ...f, flatNumber: e.target.value.toUpperCase() }))} /></Field>
              <Field label="Duration"><Select value={String(displayForm.durationSeconds)} onValueChange={(v) => setDisplayForm((f) => ({ ...f, durationSeconds: Number(v) }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="15">15 seconds</SelectItem><SelectItem value="30">30 seconds</SelectItem><SelectItem value="60">60 seconds</SelectItem></SelectContent></Select></Field>
              <div className="sm:col-span-2"><Field label="Target device"><Select value={displayForm.deviceId || "all"} onValueChange={(v) => setDisplayForm((f) => ({ ...f, deviceId: v === "all" ? "" : v }))}><SelectTrigger><SelectValue placeholder="All devices" /></SelectTrigger><SelectContent><SelectItem value="all">All devices</SelectItem>{devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.id}</SelectItem>)}</SelectContent></Select></Field></div>
            </div>
            <Button onClick={displayOwner} className="mt-5 bg-[#111827] text-white hover:bg-[#374151]">Display on Device</Button>
          </div>
          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <div className="mb-4 flex items-center gap-2"><Users className="h-5 w-5" /><h3 className="text-xl font-extrabold">Voice AI Agent</h3></div>
            <Field label="Command transcript"><Textarea value={voiceText} onChange={(e) => setVoiceText(e.target.value)} rows={4} /></Field>
            <div className="mt-3 grid gap-2 text-sm text-[#6B7280]">
              <div>Hello RP, show flat A 101</div>
              <div>Hello RP, what is the mobile number of flat A 101</div>
              <div>Hello RP, search owner Ravi</div>
              <div>Hello RP, show wing B directory</div>
              <div>Hello RP, how many pending visitors</div>
              <div>Hello RP, show emergency contacts</div>
            </div>
            {agentAnswer && (
              <div className="mt-4 rounded-sm border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-sm text-[#1D4ED8]">
                {agentAnswer}
              </div>
            )}
            {ttsStatus && (
              <div className="mt-2 text-xs font-semibold text-[#6B7280]">
                {ttsStatus}
              </div>
            )}
            {agentDisplay?.displayCards?.length > 0 && (
              <div className="mt-4 grid gap-2">
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#6B7280]">{agentDisplay.displayTitle || "Display result"}</div>
                {agentDisplay.displayCards.slice(0, 3).map((card, index) => (
                  <div key={`${card.type || "card"}-${index}`} className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                    <div className="text-sm font-extrabold text-[#111827]">{index + 1}. {card.title || "Result"}</div>
                    {card.subtitle && <div className="mt-1 text-sm text-[#4B5563]">{card.subtitle}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button onClick={() => runVoiceSearch()} className="bg-[#111827] text-white hover:bg-[#374151]"><Volume2 className="mr-2 h-4 w-4" /> Ask Agent</Button>
              <Button type="button" variant="outline" onClick={startPcMicAgent} className={listening ? "border-emerald-500 text-emerald-700" : ""}><Mic className="mr-2 h-4 w-4" /> {listening ? "Listening..." : "Use PC Mic"}</Button>
              {agentAnswer && <Button type="button" variant="outline" onClick={() => speakAnswer(agentAnswer)}><Volume2 className="mr-2 h-4 w-4" /> Speak Again</Button>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={billEditorOpen} onOpenChange={(open) => {
        setBillEditorOpen(open);
        if (!open) setEditingBill(null);
      }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit bill {editingBill?.billNumber || ""}</DialogTitle>
          </DialogHeader>
          {editingBill && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4 text-sm md:grid-cols-4">
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Flat</div><div className="font-bold">{editingBill.wing} {editingBill.flatNumber}</div></div>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Owner</div><div className="font-bold">{editingBill.ownerName}</div></div>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Month</div><div className="font-bold">{editingBill.billingMonth}</div></div>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Status</div><div className="font-bold">{editingBill.status}</div></div>
              </div>

              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[240px]">Title</TableHead>
                      <TableHead className="min-w-[150px]">Category</TableHead>
                      <TableHead className="min-w-[130px] text-right">Amount</TableHead>
                      <TableHead className="w-[70px] text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {billLineDrafts.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Input value={line.name} onChange={(e) => updateBillLineDraft(line.id, { name: e.target.value })} placeholder="Line title" />
                          <div className="mt-1 text-[11px] text-[#9CA3AF]">{line.code || "MANUAL"}</div>
                        </TableCell>
                        <TableCell>
                          <Select value={line.category || "regular"} onValueChange={(v) => updateBillLineDraft(line.id, { category: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="regular">Regular charge</SelectItem>
                              <SelectItem value="opening">Opening outstanding</SelectItem>
                              <SelectItem value="opening_credit">Member credit adjusted</SelectItem>
                              <SelectItem value="utility">Utility</SelectItem>
                              <SelectItem value="fund">Fund</SelectItem>
                              <SelectItem value="adjustment">Penalty / adjustment</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input className="text-right font-mono" type="number" min={["opening_credit", "adjustment"].includes(line.category) ? undefined : "0"} value={line.amount} onChange={(e) => updateBillLineDraft(line.id, { amount: e.target.value, rate: e.target.value, allocation: "manual", quantity: 1 })} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button type="button" variant="outline" size="sm" onClick={() => removeBillLineDraft(line.id)} disabled={billLineDrafts.length <= 1}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Button type="button" variant="outline" onClick={addBillLineDraft}><Plus className="mr-2 h-4 w-4" /> Add penalty / charge line</Button>

              <div className="grid gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4 text-sm md:grid-cols-5">
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Current charges</div><div className="font-mono text-lg font-bold">₹ {Number(billDraftCurrentCharges || 0).toLocaleString()}</div></div>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Opening dues</div><div className="font-mono text-lg font-bold">₹ {Number(billDraftOpening || 0).toLocaleString()}</div></div>
                <Field label="Interest charged"><Input className="font-mono" type="number" min="0" step="1" value={billInterestDraft} onChange={(e) => setBillInterestDraft(e.target.value)} /></Field>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Total due</div><div className="font-mono text-lg font-bold">₹ {Number(billDraftTotal || 0).toLocaleString()}</div></div>
                <div><div className="text-xs uppercase tracking-[0.16em] text-[#6B7280]">Balance</div><div className="font-mono text-lg font-bold">₹ {Number(billDraftBalance || 0).toLocaleString()}</div></div>
              </div>

              <Field label="Edit note">
                <Input value={billEditReason} onChange={(e) => setBillEditReason(e.target.value)} placeholder="Example: Sinking fund changed for this flat, wrong parking penalty added" />
              </Field>
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Saving makes this bill manually edited, so Generate Monthly Bills will not overwrite this specific bill.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillEditorOpen(false)}>Close</Button>
            <Button disabled={busy || !editingBill} onClick={saveBillLines} className="bg-[#111827] text-white hover:bg-[#374151]">Save bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editingNotice ? "Edit notice / alert" : "Add notice / alert"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title"><Input value={noticeForm.title} onChange={(e) => setNoticeForm((f) => ({ ...f, title: e.target.value }))} /></Field>
            <Field label="Category">
              <Select value={noticeForm.category} onValueChange={(v) => setNoticeForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="notice">Notice</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="emergency">Emergency Warning</SelectItem>
                  <SelectItem value="maintenance">Maintenance Notice</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select value={noticeForm.severity} onValueChange={(v) => setNoticeForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Image URL"><Input value={noticeForm.image_url} onChange={(e) => setNoticeForm((f) => ({ ...f, image_url: e.target.value }))} /></Field>
            <div className="sm:col-span-2"><Field label="Message"><Textarea rows={5} value={noticeForm.body} onChange={(e) => setNoticeForm((f) => ({ ...f, body: e.target.value }))} placeholder="Example: Water supply will be unavailable today from 2 PM to 4 PM due to tank cleaning." /></Field></div>
            <Field label="Active"><div className="flex h-10 items-center gap-3"><Switch checked={noticeForm.active} onCheckedChange={(v) => setNoticeForm((f) => ({ ...f, active: v }))} /> Active</div></Field>
            <Field label="Validity">
              <Select value={noticeValidity} onValueChange={setNoticeValidity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="infinite">Never expires</SelectItem>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="custom">Custom date...</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {noticeValidity === "custom" && (
              <Field label="Expires on"><Input type="date" value={noticeCustomDate} onChange={(e) => setNoticeCustomDate(e.target.value)} /></Field>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setNoticeOpen(false)}>Cancel</Button><Button disabled={busy} onClick={saveNotice} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ownerOpen} onOpenChange={setOwnerOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle>{editingOwner ? "Edit owner entry" : "Add owner entry"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Wing"><Input value={ownerForm.wing} onChange={(e) => setOwnerForm((f) => ({ ...f, wing: e.target.value.toUpperCase() }))} /></Field>
            <Field label="Floor"><Input value={ownerForm.floor} onChange={(e) => setOwnerForm((f) => ({ ...f, floor: e.target.value }))} /></Field>
            <Field label="Flat number"><Input value={ownerForm.flatNumber} onChange={(e) => {
              const nextFlat = e.target.value.toUpperCase();
              setOwnerForm((f) => ({ ...f, flatNumber: nextFlat, intercomNumber: f.intercomNumber || nextFlat }));
            }} /></Field>
            <Field label="Area sq ft"><Input type="number" min="0" value={ownerForm.areaSqFt || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, areaSqFt: e.target.value }))} placeholder="Example: 650" /></Field>
            <Field label="Owner name"><Input value={ownerForm.ownerName} onChange={(e) => setOwnerForm((f) => ({ ...f, ownerName: e.target.value }))} /></Field>
            <Field label="Owner mobile"><Input value={ownerForm.ownerMobile} onChange={(e) => setOwnerForm((f) => ({ ...f, ownerMobile: e.target.value }))} /></Field>
            <Field label="Unit type">
              <Select value={ownerForm.unitType || "residential"} onValueChange={(unitType) => setOwnerForm((f) => ({ ...f, unitType }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential flat</SelectItem>
                  <SelectItem value="shop">Shop</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Occupancy">
              <Select value={ownerForm.occupancyStatus || "owner_occupied"} onValueChange={(occupancyStatus) => setOwnerForm((f) => ({ ...f, occupancyStatus }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner_occupied">Owner occupied</SelectItem>
                  <SelectItem value="tenant_occupied">Tenant occupied</SelectItem>
                  <SelectItem value="vacant">Vacant</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Occupancy effective from"><Input type="date" value={ownerForm.occupancyEffectiveFrom || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, occupancyEffectiveFrom: e.target.value }))} /></Field>
            <Field label="Tenant deed status">
              <Select disabled={ownerForm.occupancyStatus !== "tenant_occupied"} value={ownerForm.tenantDeedStatus || "pending"} onValueChange={(tenantDeedStatus) => setOwnerForm((f) => ({ ...f, tenantDeedStatus, tenantDeedSubmittedOn: tenantDeedStatus === "submitted" ? f.tenantDeedSubmittedOn : "" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="submitted">Submitted</SelectItem></SelectContent>
              </Select>
            </Field>
            {ownerForm.occupancyStatus === "tenant_occupied" && ownerForm.tenantDeedStatus === "submitted" && <Field label="Deed submitted on"><Input type="date" value={ownerForm.tenantDeedSubmittedOn || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, tenantDeedSubmittedOn: e.target.value }))} /></Field>}
            <Field label="Secondary mobile"><Input value={ownerForm.ownerSecondaryMobile} onChange={(e) => setOwnerForm((f) => ({ ...f, ownerSecondaryMobile: e.target.value }))} /></Field>
            <Field label="Visitor call first">
              <Select value={ownerForm.preferredVisitorMobile || "primary"} onValueChange={(v) => setOwnerForm((f) => ({ ...f, preferredVisitorMobile: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary mobile</SelectItem>
                  <SelectItem value="secondary">Secondary mobile</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Owner email"><Input value={ownerForm.ownerEmail} onChange={(e) => setOwnerForm((f) => ({ ...f, ownerEmail: e.target.value }))} /></Field>
            <Field label="Opening balance type">
              <Select value={ownerForm.openingOutstandingSide || "debit"} onValueChange={(openingOutstandingSide) => setOwnerForm((f) => ({
                ...f,
                openingOutstandingSide,
                openingOutstanding: "",
                openingPrincipalOutstanding: "",
                openingInterestOutstanding: "",
              }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">DR · Owner owes society</SelectItem>
                  <SelectItem value="credit">CR · Owner has advance</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {ownerForm.openingOutstandingSide === "credit" ? (
              <Field label="Opening credit / advance"><Input type="number" min="0" value={ownerForm.openingOutstanding || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, openingOutstanding: e.target.value }))} placeholder="0" /></Field>
            ) : (
              <>
                <Field label="Opening principal outstanding"><Input type="number" min="0" value={ownerForm.openingPrincipalOutstanding || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, openingPrincipalOutstanding: e.target.value }))} placeholder="0" /></Field>
                <Field label="Opening interest outstanding"><Input type="number" min="0" value={ownerForm.openingInterestOutstanding || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, openingInterestOutstanding: e.target.value }))} placeholder="0" /></Field>
              </>
            )}
            <Field label="Opening as on"><Input type="date" value={ownerForm.openingOutstandingAsOf || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, openingOutstandingAsOf: e.target.value }))} /></Field>
            <div className="sm:col-span-2"><Field label="Opening note"><Input value={ownerForm.openingOutstandingNote || ""} onChange={(e) => setOwnerForm((f) => ({ ...f, openingOutstandingNote: e.target.value }))} placeholder="Balance carried forward from previous billing system" /></Field></div>
            {chargeHeads.some((head) => head.active !== false && (head.unitGroup || "flat") === selectedOwnerChargeGroup) && (
              <div className="sm:col-span-2 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-4">
                <div className="font-semibold text-[#111827]">Charge exceptions for this unit</div>
                <p className="mt-1 text-xs text-[#6B7280]">Auto follows each charge head's rule. Use an exception only when this particular unit must be treated differently.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {chargeHeads.filter((head) => head.active !== false && (head.unitGroup || "flat") === selectedOwnerChargeGroup).map((head) => (
                    <Field key={head.id} label={head.name || head.code}>
                      <Select
                        value={(ownerForm.chargeApplicabilityOverrides || {})[head.id] || "auto"}
                        onValueChange={(value) => setOwnerForm((form) => {
                          const overrides = { ...(form.chargeApplicabilityOverrides || {}) };
                          if (value === "auto") delete overrides[head.id];
                          else overrides[head.id] = value;
                          return { ...form, chargeApplicabilityOverrides: overrides };
                        })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto — follow charge rule</SelectItem>
                          <SelectItem value="include">Force apply</SelectItem>
                          <SelectItem value="exclude">Exclude</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  ))}
                </div>
              </div>
            )}
            <Field label="Committee role">
              <Select value={ownerForm.committeeRole || "none"} onValueChange={(v) => setOwnerForm((f) => ({ ...f, committeeRole: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{committeeRoles.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Committee title"><Input value={ownerForm.committeeTitle} onChange={(e) => setOwnerForm((f) => ({ ...f, committeeTitle: e.target.value }))} placeholder="Optional custom title" /></Field>
            <Field label="Tenant name"><Input value={ownerForm.tenantName} onChange={(e) => setOwnerForm((f) => ({ ...f, tenantName: e.target.value }))} /></Field>
            <Field label="Tenant mobile"><Input value={ownerForm.tenantMobile} onChange={(e) => setOwnerForm((f) => ({ ...f, tenantMobile: e.target.value }))} /></Field>
            <Field label="Parking number"><Input value={ownerForm.parkingNumber} onChange={(e) => setOwnerForm((f) => ({ ...f, parkingNumber: e.target.value }))} /></Field>
            <Field label="Vehicle number"><Input value={ownerForm.vehicleNumber} onChange={(e) => setOwnerForm((f) => ({ ...f, vehicleNumber: e.target.value }))} /></Field>
            <Field label="Cycles"><Input type="number" min="0" value={ownerForm.vehicleCycleCount} onChange={(e) => setOwnerForm((f) => ({ ...f, vehicleCycleCount: e.target.value }))} placeholder="0" /></Field>
            <div className="sm:col-span-2 space-y-4 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-4">
              {[{ type: "2w", title: "2-wheelers" }, { type: "4w", title: "4-wheelers" }].map(({ type, title }) => {
                const rows = ownerForm.vehicles.filter((v) => v.type === type);
                return (
                  <div key={type}>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">{title} ({rows.length})</div>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setOwnerForm((f) => ({ ...f, vehicles: [...f.vehicles, blankVehicle(type)] }))}
                      ><Plus className="mr-1 h-3 w-3" /> Add {type === "2w" ? "2-wheeler" : "4-wheeler"}</Button>
                    </div>
                    {rows.length === 0 && <div className="text-sm text-[#9CA3AF]">None added</div>}
                    <div className="space-y-2">
                      {rows.map((v) => (
                        <div key={v.id} className="flex items-center gap-2">
                          <Input
                            className="flex-1"
                            value={v.number}
                            disabled={type === "2w" && v.nonRtoEv}
                            placeholder={type === "2w" && v.nonRtoEv ? "No RTO plate (EV)" : "Registration number"}
                            onChange={(e) => setOwnerForm((f) => ({ ...f, vehicles: f.vehicles.map((x) => (x.id === v.id ? { ...x, number: e.target.value } : x)) }))}
                          />
                          {type === "2w" && (
                            <label className="flex shrink-0 items-center gap-1.5 text-xs text-[#6B7280]">
                              <Switch
                                checked={v.nonRtoEv}
                                onCheckedChange={(checked) => setOwnerForm((f) => ({ ...f, vehicles: f.vehicles.map((x) => (x.id === v.id ? { ...x, nonRtoEv: checked, number: checked ? "" : x.number } : x)) }))}
                              />
                              Non-RTO EV
                            </label>
                          )}
                          <Button type="button" variant="outline" size="sm" onClick={() => setOwnerForm((f) => ({ ...f, vehicles: f.vehicles.filter((x) => x.id !== v.id) }))}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-[#9CA3AF]">Cycles and non-RTO EV scooters don't need a registration number - parking charges are still billed per vehicle if a parking charge head is set up for that type.</p>
            </div>
            <Field label="Owner photo">
              <div className="flex items-center gap-3">
                {ownerForm.ownerPhoto ? <img src={resolveMediaUrl(ownerForm.ownerPhoto)} alt="" className="h-12 w-12 rounded-sm object-cover" /> : <div className="h-12 w-12 rounded-sm bg-[#EEF2F7]" />}
                <label className="inline-flex h-10 cursor-pointer items-center rounded-sm border border-[#D1D5DB] px-3 text-sm font-semibold hover:bg-[#F9FAFB]">
                  Attach photo
                  <input className="hidden" type="file" accept="image/*" onChange={(e) => uploadOwnerPhoto(e.target.files?.[0])} />
                </label>
                {ownerForm.ownerPhoto && <Button type="button" variant="outline" size="sm" onClick={() => setOwnerForm((f) => ({ ...f, ownerPhoto: "" }))}>Remove</Button>}
              </div>
            </Field>
            <Field label="Intercom / extension"><Input value={ownerForm.intercomNumber} onChange={(e) => setOwnerForm((f) => ({ ...f, intercomNumber: e.target.value }))} placeholder={ownerForm.flatNumber || "Same as flat number"} /></Field>
            <Field label="Emergency contact"><Input value={ownerForm.emergencyContact} onChange={(e) => setOwnerForm((f) => ({ ...f, emergencyContact: e.target.value }))} /></Field>
            <Field label="Active"><div className="flex h-10 items-center gap-3"><Switch checked={ownerForm.isActive} onCheckedChange={(v) => setOwnerForm((f) => ({ ...f, isActive: v }))} /> Active</div></Field>
            <div className="sm:col-span-2"><Field label="Family members"><Textarea value={ownerForm.familyMembers} onChange={(e) => setOwnerForm((f) => ({ ...f, familyMembers: e.target.value }))} placeholder="Comma separated names" /></Field></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOwnerOpen(false)}>Cancel</Button><Button disabled={busy} onClick={saveOwner} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>Import owner directory CSV</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">Headers supported: wing, floor, flatNumber, areaSqFt, unitType (residential/shop/office/other), occupancyStatus (owner_occupied/tenant_occupied/vacant), occupancyEffectiveFrom, tenantDeedStatus (pending/submitted), tenantDeedSubmittedOn, ownerName, ownerMobile, ownerSecondaryMobile, preferredVisitorMobile, ownerEmail, tenantName, tenantMobile, parkingNumber, vehicleNumber, vehicle2wCount, vehicle4wCount, vehicleCycleCount, intercomNumber, emergencyContact, committeeRole, committeeTitle, openingPrincipalOutstanding, openingInterestOutstanding, openingOutstanding (credit/legacy), openingOutstandingSide (debit/credit), openingOutstandingNote, openingOutstandingAsOf, isActive.</p>
          <Textarea rows={10} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="wing,floor,flatNumber,areaSqFt,ownerName,ownerMobile" />
          <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button><Button onClick={importDirectory} className="bg-[#111827] text-white hover:bg-[#374151]">Import</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
